/**
 * Drawer for conducting practitioner assessments during a session.
 * Shows "Which assessment would you like to conduct?" with list from practitioner's allowed assessment lists.
 * On select: book/block (API); if payment pending show refresh; else open runner. State can persist for resume.
 */
import AssignmentIcon from '@mui/icons-material/Assignment'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Switch,
  Typography,
  alpha,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { isApiDebugModeQueryActive } from 'src/consumer/services/apiDomainConfig'

/** When true, show "Include non-runnable", "Include self-conducted", and the session/conduct helper text. Only when page URL has `?shiva=shakti` (API debug mode). */
function useShowApiDebugModeUrlUi() {
  if (typeof window === 'undefined') return false
  return isApiDebugModeQueryActive(window.location.search)
}

dayjs.extend(utc)
import PractitionerAssessmentAnswerSheetModal from './PractitionerAssessmentAnswerSheetModal'
import PractitionerAssessmentAnswersView from './PractitionerAssessmentAnswersView'
import CustomProtocolAssessmentRunner from './CustomProtocolAssessmentRunner'
import PractitionerAssessmentResultView from './PractitionerAssessmentResultView'
import PractitionerAssessmentRunner from './PractitionerAssessmentRunner'
import {
  getPractitionerAssessmentAccessDetails,
  getEventAssessments,
  getAvailableAssessments,
  getAssessmentRuns,
  getRunContext,
  getBookingAvailability,
  bookAssessment,
  getAssessmentDetail,
  markBookedEventAsStarted,
} from 'src/mentor/services/practitionerAssessmentService'
import { getMainLanguageCode } from 'src/mentor/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

const DRAFT_KEY_PREFIX = 'practitioner-assessment-draft-'
const CONSENT_STORAGE_KEY_PREFIX = 'practitioner-assessment-consent-'
const ASSESSMENT_EVENT_MAP_KEY_PREFIX = 'practitioner-assessment-event-map-'
const ASSESSMENT_REF_TYPE = 'assessment'

function isCustomProtocolRunnerType(runnerType) {
  return String(runnerType || '').toUpperCase() === 'CUSTOM_PROTOCOL'
}

function isCustomProtocolAssessmentSpec(assessmentSpec) {
  return isCustomProtocolRunnerType(assessmentSpec?.runnerType)
}

function parsePatientAgeYears(value) {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value
  }
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  if (!normalized) return null
  const directNumber = Number(normalized)
  if (Number.isFinite(directNumber) && directNumber >= 0) return directNumber
  const match = normalized.match(/(\d+(?:\.\d+)?)/)
  if (!match) return null
  const numericValue = Number(match[1])
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null
}

function normalizeBookedEventInfoForAssessment(bookedEventInfo) {
  if (!bookedEventInfo || typeof bookedEventInfo !== 'object') return bookedEventInfo

  const patientInfoData = bookedEventInfo?.patientInfoData ?? {}
  const patientInfo = bookedEventInfo?.patientInfo ?? {}
  const patient = bookedEventInfo?.patient ?? {}
  const patientVital = bookedEventInfo?.patientPrefill?.vital ?? {}

  const patientAgeYears =
    parsePatientAgeYears(patientInfoData?.age) ??
    parsePatientAgeYears(patientInfo?.age) ??
    parsePatientAgeYears(patient?.age) ??
    parsePatientAgeYears(bookedEventInfo?.patientAge) ??
    parsePatientAgeYears(bookedEventInfo?.age)

  const directDateOfBirth =
    patientInfoData?.dateOfBirth ??
    patientInfo?.dateOfBirth ??
    patientInfo?.vital?.dateOfBirth ??
    patient?.dateOfBirth ??
    patient?.vital?.dateOfBirth ??
    bookedEventInfo?.patientDateOfBirth ??
    bookedEventInfo?.dateOfBirth ??
    patientVital?.dateOfBirth ??
    null

  const derivedEstimatedDateOfBirth =
    directDateOfBirth == null && patientAgeYears != null
      ? dayjs().subtract(patientAgeYears, 'year').startOf('day').format('YYYY-MM-DD 00:00:00')
      : null

  const dateOfBirth = directDateOfBirth ?? derivedEstimatedDateOfBirth

  const dobProvided =
    patientInfoData?.dobProvided ??
    patientInfo?.dobProvided ??
    patientInfo?.vital?.dobProvided ??
    patient?.dobProvided ??
    patient?.vital?.dobProvided ??
    bookedEventInfo?.dobProvided ??
    patientVital?.dobProvided ??
    null

  const gender =
    patientInfoData?.gender ??
    patientInfo?.gender ??
    patient?.gender ??
    bookedEventInfo?.patientGender ??
    bookedEventInfo?.gender ??
    patientVital?.gender ??
    null

  const name =
    patientInfoData?.name ??
    patientInfo?.name ??
    patient?.name ??
    bookedEventInfo?.patientName ??
    bookedEventInfo?.userName ??
    null

  const patientNumber =
    patientInfoData?.patientNumber ??
    patientInfo?.patientNumber ??
    patient?.patientNumber ??
    bookedEventInfo?.patientNumber ??
    null

  return {
    ...bookedEventInfo,
    patientDateOfBirth: bookedEventInfo?.patientDateOfBirth ?? dateOfBirth ?? null,
    patientGender: bookedEventInfo?.patientGender ?? gender ?? null,
    patientName: bookedEventInfo?.patientName ?? name ?? null,
    patientNumber: bookedEventInfo?.patientNumber ?? patientNumber ?? null,
    patientInfoData: {
      ...patientInfoData,
      age: patientInfoData?.age ?? patientAgeYears ?? null,
      dateOfBirth: patientInfoData?.dateOfBirth ?? dateOfBirth ?? null,
      dobProvided: patientInfoData?.dobProvided ?? dobProvided ?? null,
      gender: patientInfoData?.gender ?? gender ?? null,
      name: patientInfoData?.name ?? name ?? null,
      patientNumber: patientInfoData?.patientNumber ?? patientNumber ?? null,
    },
    patientInfo: {
      ...patientInfo,
      age: patientInfo?.age ?? patientAgeYears ?? null,
      dateOfBirth: patientInfo?.dateOfBirth ?? dateOfBirth ?? null,
      dobProvided: patientInfo?.dobProvided ?? dobProvided ?? null,
      gender: patientInfo?.gender ?? gender ?? null,
      name: patientInfo?.name ?? name ?? null,
      patientNumber: patientInfo?.patientNumber ?? patientNumber ?? null,
    },
  }
}

/** Store practitioner consent for this assessment run (local for now; API to be designed later). */
function storeAssessmentConsent(bookedEventId, assessmentId, assessmentTitle) {
  try {
    const key = `${CONSENT_STORAGE_KEY_PREFIX}${bookedEventId}-${assessmentId}`
    const payload = {
      assessmentId,
      bookedEventId,
      assessmentTitle: assessmentTitle || 'Assessment',
      acceptedAt: new Date().toISOString(),
    }
    sessionStorage.setItem(key, JSON.stringify(payload))
  } catch (e) {}
}

/** Check if practitioner has already stored consent for this assessment (e.g. from inline launcher consent). */
function hasStoredConsent(bookedEventId, assessmentId) {
  if (!bookedEventId || !assessmentId) return false
  try {
    const key = `${CONSENT_STORAGE_KEY_PREFIX}${bookedEventId}-${assessmentId}`
    const raw = sessionStorage.getItem(key)
    return Boolean(raw && JSON.parse(raw)?.assessmentId)
  } catch (e) {
    return false
  }
}

function getDraftKey(bookedEventId, assessmentId) {
  return `${DRAFT_KEY_PREFIX}${bookedEventId}-${assessmentId}`
}

function getAssessmentEventMapKey(parentBookedEventId) {
  if (!parentBookedEventId) return null
  return `${ASSESSMENT_EVENT_MAP_KEY_PREFIX}${parentBookedEventId}`
}

function readAssessmentEventMap(parentBookedEventId) {
  const key = getAssessmentEventMapKey(parentBookedEventId)
  if (!key) return {}
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

function writeAssessmentEventMap(parentBookedEventId, mapping) {
  const key = getAssessmentEventMapKey(parentBookedEventId)
  if (!key) return
  try {
    sessionStorage.setItem(key, JSON.stringify(mapping ?? {}))
  } catch (e) {}
}

function setMappedAssessmentEventId(parentBookedEventId, assessmentId, assessmentEventBookedEventId, clinicalAssessmentId) {
  const safeAssessmentEventBookedEventId = sanitizeAssessmentEventBookedEventId(
    assessmentEventBookedEventId,
    parentBookedEventId,
  )
  if (!parentBookedEventId || !assessmentId || !safeAssessmentEventBookedEventId) return
  const currentMap = readAssessmentEventMap(parentBookedEventId)
  currentMap[assessmentId] = {
    assessmentEventBookedEventId: safeAssessmentEventBookedEventId,
    clinicalAssessmentId: clinicalAssessmentId ?? currentMap?.[assessmentId]?.clinicalAssessmentId ?? null,
    updatedAt: new Date().toISOString(),
  }
  writeAssessmentEventMap(parentBookedEventId, currentMap)
}

function getMappedAssessmentEventId(parentBookedEventId, assessmentId) {
  if (!parentBookedEventId || !assessmentId) return null
  const currentMap = readAssessmentEventMap(parentBookedEventId)
  return sanitizeAssessmentEventBookedEventId(
    currentMap?.[assessmentId]?.assessmentEventBookedEventId ?? null,
    parentBookedEventId,
  )
}

function extractAssessmentEventBookedEventIdFromRun(run, parentBookedEventId) {
  const candidate =
    run?.assessmentEventBookedEventId ??
    run?.assessmentRunView?.assessmentEventBookedEventId ??
    run?.assessmentRun?.assessmentEventBookedEventId ??
    run?.assessmentBookedEventId ??
    run?.assessmentRunView?.assessmentBookedEventId ??
    run?.assessmentRun?.assessmentBookedEventId ??
    run?.bookedEventId ??
    run?.assessmentRunView?.bookedEventId ??
    run?.assessmentRun?.bookedEventId ??
    run?.assessmentBookedEvent?.id ??
    run?.assessmentRunView?.assessmentBookedEvent?.id ??
    run?.assessmentRun?.assessmentBookedEvent?.id ??
    run?.run?.bookedEventId ??
    null
  if (!candidate) return null
  if (parentBookedEventId && candidate === parentBookedEventId) return null
  return candidate
}

function sanitizeAssessmentEventBookedEventId(candidate, parentBookedEventId) {
  if (!candidate) return null
  if (parentBookedEventId && candidate === parentBookedEventId) return null
  return candidate
}

function extractAssessmentEventBookedEventIdFromBookingData(data, parentBookedEventId) {
  const candidate =
    data?.assessmentBookedEvent?.id ??
    data?.assessmentBookedEventId ??
    data?.assessmentStep?.refId ??
    (Array.isArray(data?.assessmentSteps) ? data.assessmentSteps?.[0]?.refId : null) ??
    null
  return sanitizeAssessmentEventBookedEventId(candidate, parentBookedEventId)
}

function mergeMappedAssessmentEventsFromRuns(
  parentBookedEventId,
  runs,
  assessmentIdsByClinicalId = null,
  assessmentIdsByTitle = null,
) {
  if (!parentBookedEventId || !Array.isArray(runs) || runs.length === 0) return
  const currentMap = readAssessmentEventMap(parentBookedEventId)
  let updated = false
  for (const run of runs) {
    const assessmentId = resolveRunAssessmentIdWithFallback(
      run,
      assessmentIdsByClinicalId,
      assessmentIdsByTitle,
    )
    const eventId = extractAssessmentEventBookedEventIdFromRun(run, parentBookedEventId)
    if (!assessmentId || !eventId) continue
    currentMap[assessmentId] = {
      assessmentEventBookedEventId: eventId,
      clinicalAssessmentId: getRunClinicalAssessmentId(run) ?? currentMap?.[assessmentId]?.clinicalAssessmentId ?? null,
      updatedAt: new Date().toISOString(),
    }
    updated = true
  }
  if (updated) writeAssessmentEventMap(parentBookedEventId, currentMap)
}

function mergeMappedAssessmentEventsFromStatuses(
  parentBookedEventId,
  assessmentStatuses,
  assessmentIdsByClinicalId = null,
  assessmentIdsByTitle = null,
) {
  if (!parentBookedEventId || !Array.isArray(assessmentStatuses) || assessmentStatuses.length === 0) return
  const currentMap = readAssessmentEventMap(parentBookedEventId)
  let updated = false
  for (const statusItem of assessmentStatuses) {
    const assessmentId = resolveStatusAssessmentIdWithFallback(
      statusItem,
      assessmentIdsByClinicalId,
      assessmentIdsByTitle,
    )
    const eventId = getAssessmentStatusBookedEventId(statusItem, parentBookedEventId)
    if (!assessmentId || !eventId) continue
    currentMap[assessmentId] = {
      assessmentEventBookedEventId: eventId,
      clinicalAssessmentId: getStatusClinicalAssessmentId(statusItem) ?? currentMap?.[assessmentId]?.clinicalAssessmentId ?? null,
      updatedAt: new Date().toISOString(),
    }
    updated = true
  }
  if (updated) writeAssessmentEventMap(parentBookedEventId, currentMap)
}

function findAssessmentEventIdInParentInSteps(bookedEventInfo, parentBookedEventId, assessmentId, clinicalAssessmentId) {
  const inSteps = Array.isArray(bookedEventInfo?.inSteps) ? bookedEventInfo.inSteps : []
  for (const step of inSteps) {
    const refId = step?.refId
    if (!refId || refId === parentBookedEventId) continue
    const refType = String(step?.refType ?? '').toLowerCase()
    if (refType && refType !== ASSESSMENT_REF_TYPE) continue
    const refSubType = step?.refSubType
    if (refSubType && (refSubType === assessmentId || refSubType === clinicalAssessmentId)) return refId
  }
  return null
}

function getAssessmentBookingErrorMessage(code, fallbackMessage) {
  if (code === 4030) {
    return 'Practitioner assessment access is disabled for this account.'
  }
  if (code === 4034) {
    return 'You are not approved for this clinical assessment.'
  }
  return fallbackMessage || 'Failed to book assessment'
}

function getRunAssessmentId(run) {
  return (
    run?.assessmentId ??
    run?.assessmentRunView?.assessmentId ??
    run?.assessmentRun?.assessmentId ??
    run?.run?.assessmentId ??
    run?.assessmentRunView?.assessment?.id ??
    run?.assessmentRun?.assessment?.id ??
    null
  )
}

function getRunClinicalAssessmentId(run) {
  return (
    run?.clinicalAssessmentId ??
    run?.psychAssessmentId ??
    run?.assessmentRunView?.clinicalAssessmentId ??
    run?.assessmentRunView?.psychAssessmentId ??
    run?.assessmentRun?.clinicalAssessmentId ??
    run?.assessmentRun?.psychAssessmentId ??
    run?.run?.clinicalAssessmentId ??
    run?.run?.psychAssessmentId ??
    run?.assessmentRunView?.clinicalAssessment?.id ??
    run?.assessmentRunView?.psychAssessment?.id ??
    run?.assessmentRun?.clinicalAssessment?.id ??
    run?.assessmentRun?.psychAssessment?.id ??
    null
  )
}

function getRunAssessmentTitle(run) {
  return (
    run?.assessmentTitle ??
    run?.assessmentRunView?.assessmentTitle ??
    run?.assessmentRun?.assessmentTitle ??
    run?.psychAssessmentTitle ??
    run?.clinicalAssessmentTitle ??
    run?.assessmentRunView?.psychAssessmentTitle ??
    run?.assessmentRunView?.clinicalAssessmentTitle ??
    run?.assessmentRun?.psychAssessmentTitle ??
    run?.assessmentRun?.clinicalAssessmentTitle ??
    null
  )
}

function normalizeAssessmentStatusValue(value) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (!normalized) return null
  if (normalized === 'in_progress') return 'in-progress'
  if (normalized === 'started' || normalized === 'in-progress' || normalized === 'restarted' || normalized === 'completed') {
    return normalized
  }
  return null
}

function getRunStatus(run) {
  return normalizeAssessmentStatusValue(
    run?.status ??
      run?.assessmentRunView?.status ??
      run?.assessmentRun?.status ??
      run?.run?.status ??
      null,
  )
}

function getRunTimestampMs(run) {
  const tsCandidate =
    run?.completedAt ??
    run?.modifiedTime ??
    run?.createdTime ??
    run?.assessmentRunView?.completedAt ??
    run?.assessmentRunView?.modifiedTime ??
    run?.assessmentRunView?.createdTime ??
    run?.assessmentRun?.completedAt ??
    run?.assessmentRun?.modifiedTime ??
    run?.assessmentRun?.createdTime ??
    null
  if (!tsCandidate) return 0
  const ts = new Date(tsCandidate).getTime()
  return Number.isFinite(ts) ? ts : 0
}

function isRunCompleted(run) {
  if (!run || typeof run !== 'object') return false
  const status = getRunStatus(run)
  if (status === 'completed') return true
  const completedAt =
    run?.completedAt ??
    run?.assessmentRunView?.completedAt ??
    run?.assessmentRun?.completedAt ??
    run?.run?.completedAt ??
    null
  return Boolean(completedAt)
}

function pickPreferredRun(currentRun, nextRun) {
  if (!currentRun) return nextRun
  if (!nextRun) return currentRun
  const currentCompleted = isRunCompleted(currentRun)
  const nextCompleted = isRunCompleted(nextRun)
  if (currentCompleted !== nextCompleted) {
    return nextCompleted ? nextRun : currentRun
  }
  const currentTs = getRunTimestampMs(currentRun)
  const nextTs = getRunTimestampMs(nextRun)
  return nextTs >= currentTs ? nextRun : currentRun
}

function normalizeAssessmentTitleForLookup(value) {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase().replace(/\s+/g, ' ')
  return normalized || null
}

function buildAssessmentIdsByClinicalId(eventAssessments, availableAssessments) {
  const map = new Map()
  const addGroups = (groups) => {
    for (const group of groups || []) {
      const clinical = getClinicalAssessment(group)
      const clinicalId = clinical?.id
      if (!clinicalId) continue
      const specs = getSpecs(group)
      for (const spec of specs || []) {
        const id = specId(spec)
        if (!id) continue
        if (!map.has(clinicalId)) map.set(clinicalId, new Set())
        map.get(clinicalId).add(id)
      }
    }
  }
  addGroups(eventAssessments)
  addGroups(availableAssessments)
  return map
}

function buildAssessmentIdsByTitle(eventAssessments, availableAssessments) {
  const map = new Map()
  const addGroups = (groups) => {
    for (const group of groups || []) {
      const specs = getSpecs(group)
      for (const spec of specs || []) {
        const id = specId(spec)
        const title = normalizeAssessmentTitleForLookup(spec?.title)
        if (!id || !title) continue
        if (!map.has(title)) map.set(title, new Set())
        map.get(title).add(id)
      }
    }
  }
  addGroups(eventAssessments)
  addGroups(availableAssessments)
  return map
}

function resolveRunAssessmentId(run, assessmentIdsByClinicalId = null) {
  const explicitAssessmentId = getRunAssessmentId(run)
  if (explicitAssessmentId) return explicitAssessmentId
  const clinicalAssessmentId = getRunClinicalAssessmentId(run)
  if (!clinicalAssessmentId || !assessmentIdsByClinicalId?.has(clinicalAssessmentId)) return null
  const candidates = Array.from(assessmentIdsByClinicalId.get(clinicalAssessmentId) || [])
  if (candidates.length === 1) return candidates[0]
  return null
}

function resolveRunAssessmentIdWithFallback(run, assessmentIdsByClinicalId = null, assessmentIdsByTitle = null) {
  const resolvedFromIds = resolveRunAssessmentId(run, assessmentIdsByClinicalId)
  if (resolvedFromIds) return resolvedFromIds
  const runTitle = normalizeAssessmentTitleForLookup(getRunAssessmentTitle(run))
  if (!runTitle || !assessmentIdsByTitle?.has(runTitle)) return null
  const candidates = Array.from(assessmentIdsByTitle.get(runTitle) || [])
  if (candidates.length === 1) return candidates[0]
  return null
}

function getStatusClinicalAssessmentId(statusItem) {
  return (
    statusItem?.clinicalAssessmentId ??
    statusItem?.psychAssessmentId ??
    null
  )
}

function getStatusAssessmentId(statusItem) {
  return (
    statusItem?.assessmentId ??
    statusItem?.assessmentRunView?.assessmentId ??
    null
  )
}

function resolveStatusAssessmentIdWithFallback(
  statusItem,
  assessmentIdsByClinicalId = null,
  assessmentIdsByTitle = null,
) {
  const explicitAssessmentId = getStatusAssessmentId(statusItem)
  if (explicitAssessmentId) return explicitAssessmentId
  const clinicalAssessmentId = getStatusClinicalAssessmentId(statusItem)
  if (clinicalAssessmentId && assessmentIdsByClinicalId?.has(clinicalAssessmentId)) {
    const byClinical = Array.from(assessmentIdsByClinicalId.get(clinicalAssessmentId) || [])
    if (byClinical.length === 1) return byClinical[0]
  }
  const title = normalizeAssessmentTitleForLookup(
    statusItem?.assessmentTitle ??
      statusItem?.assessmentRunView?.assessmentTitle ??
      null,
  )
  if (!title || !assessmentIdsByTitle?.has(title)) return null
  const byTitle = Array.from(assessmentIdsByTitle.get(title) || [])
  if (byTitle.length === 1) return byTitle[0]
  return null
}

function hasCompletedRunInStatus(statusItem) {
  if (!statusItem || typeof statusItem !== 'object') return false
  if (statusItem?.hasCompletedRun === true) return true
  if (statusItem?.latestCompletedRunId) return true
  if (Array.isArray(statusItem?.completedRunIds) && statusItem.completedRunIds.length > 0) return true
  return false
}

function hasDraftRunInStatus(statusItem) {
  if (!statusItem || typeof statusItem !== 'object') return false
  if (statusItem?.hasDraftRun === true) return true
  if (statusItem?.latestDraftRunId) return true
  const statusValue = normalizeAssessmentStatusValue(statusItem?.status)
  return statusValue === 'started' || statusValue === 'in-progress' || statusValue === 'restarted'
}

function getAssessmentStatusBookedEventId(statusItem, parentBookedEventId) {
  return sanitizeAssessmentEventBookedEventId(
    statusItem?.bookedEventId ?? statusItem?.assessmentEventBookedEventId ?? null,
    parentBookedEventId,
  )
}

function getListingStatusLabel(statusItem, isCompleted) {
  const status = normalizeAssessmentStatusValue(statusItem?.status)
  if (isCompleted) {
    if (status === 'restarted') return 'Completed • Redo in progress'
    return 'Completed'
  }
  if (status === 'in-progress') return 'In progress'
  if (status === 'started') return 'Started'
  if (status === 'restarted') return 'Redo in progress'
  return null
}

function normalizeRun(
  run,
  fallbackAssessmentId = null,
  fallbackAssessmentTitle = null,
  assessmentIdsByClinicalId = null,
  assessmentIdsByTitle = null,
) {
  if (!run || typeof run !== 'object') return null
  const assessmentId =
    resolveRunAssessmentIdWithFallback(run, assessmentIdsByClinicalId, assessmentIdsByTitle) ??
    fallbackAssessmentId ??
    null
  return {
    ...run,
    assessmentId,
    assessmentTitle:
      run?.assessmentTitle ??
      run?.assessmentRunView?.assessmentTitle ??
      run?.assessmentRun?.assessmentTitle ??
      run?.psychAssessmentTitle ??
      run?.clinicalAssessmentTitle ??
      run?.assessmentRunView?.psychAssessmentTitle ??
      run?.assessmentRunView?.clinicalAssessmentTitle ??
      run?.assessmentRun?.psychAssessmentTitle ??
      run?.assessmentRun?.clinicalAssessmentTitle ??
      fallbackAssessmentTitle ??
      null,
    assessmentRunId:
      run?.assessmentRunId ??
      run?.id ??
      run?.assessmentRun?.assessmentRunId ??
      run?.assessmentRun?.id ??
      run?.assessmentRunView?.assessmentRunId ??
      run?.assessmentRunView?.id ??
      null,
    status: getRunStatus(run),
  }
}

function mergeRunsByAssessmentId(
  existingRuns,
  incomingRuns,
  assessmentIdsByClinicalId = null,
  assessmentIdsByTitle = null,
) {
  const byAssessmentId = new Map()
  ;(existingRuns ?? []).forEach((run) => {
    const normalized = normalizeRun(run, null, null, assessmentIdsByClinicalId, assessmentIdsByTitle)
    const assessmentId = resolveRunAssessmentIdWithFallback(
      normalized,
      assessmentIdsByClinicalId,
      assessmentIdsByTitle,
    )
    if (!assessmentId) return
    const existing = byAssessmentId.get(assessmentId) ?? null
    byAssessmentId.set(assessmentId, pickPreferredRun(existing, normalized))
  })
  ;(incomingRuns ?? []).forEach((run) => {
    const normalized = normalizeRun(run, null, null, assessmentIdsByClinicalId, assessmentIdsByTitle)
    const assessmentId = resolveRunAssessmentIdWithFallback(
      normalized,
      assessmentIdsByClinicalId,
      assessmentIdsByTitle,
    )
    if (!assessmentId) return
    const existing = byAssessmentId.get(assessmentId) ?? null
    byAssessmentId.set(assessmentId, pickPreferredRun(existing, normalized))
  })
  return Array.from(byAssessmentId.values())
}

function getAssessmentSpecCompletedRunId(spec) {
  if (!spec || typeof spec !== 'object') return null
  return (
    spec?.latestCompletedRunId ??
    spec?.completedRunId ??
    spec?.lastCompletedRunId ??
    spec?.latestCompletedAssessmentRun?.id ??
    spec?.latestCompletedAssessmentRun?.assessmentRunId ??
    spec?.completedAssessmentRun?.id ??
    spec?.completedAssessmentRun?.assessmentRunId ??
    null
  )
}

function isAssessmentSpecCompleted(spec) {
  if (!spec || typeof spec !== 'object') return false
  if (
    spec?.assessmentCompleted === true ||
    spec?.completed === true ||
    spec?.runCompleted === true ||
    spec?.hasCompletedRun === true
  ) {
    return true
  }
  return Boolean(getAssessmentSpecCompletedRunId(spec))
}

function extractRunAndReport(
  payload,
  fallbackAssessmentId,
  fallbackAssessmentTitle,
  assessmentIdsByClinicalId = null,
  assessmentIdsByTitle = null,
) {
  if (!payload || typeof payload !== 'object') return { run: null, report: null }
  const data = payload?.data && typeof payload.data === 'object' ? payload.data : payload
  const report = data?.assessmentReport ?? data?.report ?? null
  const runView = data?.assessmentRunView
  const runCore =
    data?.assessmentRun ??
    data?.run ??
    (data?.assessmentId || data?.id || data?.assessmentRunId ? data : null)
  const rawRun = runView && runCore ? { ...runView, ...runCore } : (runView ?? runCore)
  if (!rawRun) return { run: null, report }
  const run = normalizeRun(
    rawRun,
    fallbackAssessmentId,
    fallbackAssessmentTitle,
    assessmentIdsByClinicalId,
    assessmentIdsByTitle,
  )
  return { run, report }
}

/** Flatten assessmentLists to unique assessments (by id) for display */
function getAllowedAssessments(assessmentLists) {
  const byId = new Map()
  for (const list of assessmentLists || []) {
    for (const a of list.assessments || []) {
      if (a?.id && !byId.has(a.id)) byId.set(a.id, { id: a.id, slug: a.slug, title: a.title || a.slug })
    }
  }
  return Array.from(byId.values())
}

/** clinicalAssessment is the new name; psychAssessment remains for backward compatibility */
function getClinicalAssessment(group) {
  return group?.clinicalAssessment ?? group?.psychAssessment
}

/**
 * Find clinical/psych assessment + full spec for an assessmentId from eventAssessments.
 * Includes new doc fields: appointmentEventTypeId, appointmentEventType, eventTypeConfigured, eventTypeId, eventType.
 */
/** API may return assessments or assessmentTests; specs may have assessmentId or id */
function getSpecs(group) {
  return group?.assessments ?? group?.assessmentTests ?? []
}
function specId(spec) {
  return spec?.assessmentId ?? spec?.id
}

/** Build the "found" object from a group (shared by spec match and link-only match) */
function buildFoundFromGroup(group, clinical, assessmentSpec) {
  const specs = getSpecs(group)
  const appointmentItemsType = group?.appointmentItemsType
  const isBundle = appointmentItemsType === 'bundle' || specs.length > 1
  return {
    clinicalAssessment: clinical,
    psychAssessment: clinical,
    assessmentSpec,
    appointmentConfigured: group?.appointmentConfigured ?? true,
    reason: group?.appointmentReason ?? group?.reason ?? null,
    appointmentPrice: group?.appointmentPrice,
    appointmentCurrency: group?.appointmentCurrency,
    appointmentItemsType,
    isBundle,
    appointmentEventTypeId: group?.appointmentEventTypeId ?? null,
    appointmentEventType: group?.appointmentEventType ?? null,
    eventTypeConfigured: group?.eventTypeConfigured ?? false,
    eventTypeId: group?.eventTypeId ?? null,
    eventType: group?.eventType ?? null,
    runnable: group?.runnable,
    runnableReason: group?.runnableReason ?? group?.runnable_reason ?? null,
  }
}

function findEventAssessment(eventAssessments, assessmentId) {
  for (const group of eventAssessments || []) {
    const clinical = getClinicalAssessment(group)
    const specs = getSpecs(group)
    const spec = specs.find((a) => specId(a) === assessmentId)
    if (spec && clinical) {
      const assessmentSpec = { ...spec, assessmentId: spec?.assessmentId ?? spec?.id }
      return buildFoundFromGroup(group, clinical, assessmentSpec)
    }
  }
  // Group with no assessments array (e.g. non-runnable "linked assessments not found") — match by assessmentLinks
  for (const group of eventAssessments || []) {
    const clinical = getClinicalAssessment(group)
    const links = clinical?.assessmentLinks ?? []
    const link = links.find((l) => l?.assessmentId === assessmentId)
    if (link && clinical) {
      const assessmentSpec = { assessmentId: link.assessmentId, title: clinical?.title }
      return buildFoundFromGroup(group, clinical, assessmentSpec)
    }
  }
  return null
}

/**
 * Build API-derived list from event + available (same assessmentId format as APIs).
 * Returns { id, title, found, source, clinicalAssessmentId }[], deduped by id (event wins).
 */
function getDrawerListFromApi(eventAssessments, availableAssessments) {
  const byId = new Map()
  const add = (groups, source) => {
    const list = source === 'event' ? eventAssessments : availableAssessments
    for (const group of groups || []) {
      const clinical = getClinicalAssessment(group)
      const clinicalAssessmentId = clinical?.id ?? null
      const specs = getSpecs(group)
      if (specs.length > 0) {
        for (const spec of specs) {
          const id = specId(spec)
          if (!id || byId.has(id)) continue
          const title = spec?.title ?? clinical?.title ?? 'Assessment'
          const found = findEventAssessment(list, id)
          if (found) byId.set(id, { id, title, found, source, clinicalAssessmentId })
        }
      } else {
        // No assessments array (e.g. non-runnable "linked assessments not found") — add one row per assessmentLink
        const links = clinical?.assessmentLinks ?? []
        for (const link of links) {
          const id = link?.assessmentId
          if (!id || byId.has(id)) continue
          const title = clinical?.title ?? 'Assessment'
          const found = findEventAssessment(list, id)
          if (found) byId.set(id, { id, title, found, source, clinicalAssessmentId })
        }
      }
    }
  }
  add(eventAssessments, 'event')
  add(availableAssessments, 'available')
  return Array.from(byId.values())
}

/**
 * Merge allowed list (access/details) with API list so all allowed assessments are visible.
 * Match by clinicalAssessment.id: allowed assessment id === eventAssessments[].clinicalAssessment.id.
 * Returns { id, title, found, source: 'event'|'available'|'notConfigured' }[].
 */
function getDrawerAssessmentList(allowedAssessments, apiList) {
  const apiByClinicalId = new Map()
  for (const item of apiList) {
    const clinicalId = item.clinicalAssessmentId
    if (!clinicalId) continue
    if (!apiByClinicalId.has(clinicalId)) apiByClinicalId.set(clinicalId, [])
    apiByClinicalId.get(clinicalId).push(item)
  }
  const result = []
  for (const a of allowedAssessments || []) {
    const matching = apiByClinicalId.get(a.id) ?? []
    if (matching.length > 0) {
      for (const item of matching) result.push(item)
    } else {
      result.push({
        id: a.id,
        title: a.title || a.slug || 'Assessment',
        found: null,
        source: 'notConfigured',
      })
    }
  }
  return result
}

const DEFAULT_DRAWER_WIDTH = 640
const CUSTOM_PROTOCOL_DRAWER_WIDTH = 880

export default function PractitionerAssessmentDrawer({
  open,
  onClose,
  bookedEventId,
  bookedEventInfo,
  onRunSubmitted,
  /** Pre-loaded AssessmentReport for the session (e.g. from auxiliaryData); used for free-form notes on result page */
  assessmentReport,
  /** Required for saving practitioner notes to AssessmentReport */
  patientId,
  mentorId,
  /** Called after practitioner notes are saved so parent can refresh event data */
  onAssessmentReportSaved,
  /** When true, render only inner content (no Drawer wrapper). Parent supplies the Drawer for custom width. */
  contentOnly = false,
  /** Width of the drawer when not contentOnly. Ignored when contentOnly. */
  drawerWidth = DEFAULT_DRAWER_WIDTH,
  /** When true, bookedEventId is the assessment event (e.g. assessment report page). Use assessmentEventBookedEventId for runs/run. */
  isAssessmentEventPage = false,
  /** Pre-loaded eventAssessments from parent (e.g. auxiliary data). When set, skip getEventAssessments/getAvailableAssessments. */
  initialEventAssessments,
  /** Pre-loaded assessmentRuns from parent (e.g. assessmentReport.assessmentRuns). When set with initialEventAssessments, skip getAssessmentRuns. */
  initialAssessmentRuns,
  /** When true, render only the assessment list (for inline launcher). Clicks call onListActionClick instead of opening runner/result in-place. */
  renderListOnly = false,
  /** When renderListOnly, called when user clicks Start/Resume/View Result/Edit Report/View Answers/Open (id, title, action, completedRunId, clinicalAssessmentTitle). Action is 'start'|'resume'|'result'|'edit'|'answers'|null (null = open to list). */
  onListActionClick,
  /** When set, after load the drawer will open directly to this assessment and run the given action (used by launcher action drawer). */
  openWithAssessmentId,
  /** openWithAction: 'start'|'resume'|'result'|'edit'|'answers'|null. Null = show list only. */
  openWithAction,
  /** For openWithAction 'result', optional completed run id. */
  openWithCompletedRunId,
  /** Called after openWith* has been applied so parent can clear those props. */
  onOpenWithHandled,
  /** Optional heading for the drawer (e.g. Clinical Assessment title for launcher action drawer). When set, used instead of "Assessments". */
  drawerHeading,
}) {
  const [loading, setLoading] = useState(false)
  const [accessAllowed, setAccessAllowed] = useState(false)
  const [allowedAssessments, setAllowedAssessments] = useState([]) // { id, slug, title }[]
  const [eventAssessments, setEventAssessments] = useState([])
  const [availableAssessments, setAvailableAssessments] = useState([]) // same shape as eventAssessments
  const [bookingAvailability, setBookingAvailability] = useState({ appointmentConfigured: true, reason: null })
  const [assessmentRuns, setAssessmentRuns] = useState([])
  const [assessmentStatuses, setAssessmentStatuses] = useState([])
  const [assessmentRunsReport, setAssessmentRunsReport] = useState(null)
  const [selectedRun, setSelectedRun] = useState(null) // { clinicalAssessment, psychAssessment, assessmentSpec, ... } when running
  const [bookingInProgress, setBookingInProgress] = useState(false)
  const [startingRunner, setStartingRunner] = useState(false)
  /** Display info for "Preparing the assessment" overlay: title, description, icon/coverIcon URLs */
  const [loadingAssessmentDisplay, setLoadingAssessmentDisplay] = useState(null)
  const [paymentPending, setPaymentPending] = useState(null) // { assessmentId, assessmentTitle } when waiting for payment
  const [resumedRun, setResumedRun] = useState(null) // restored from sessionStorage
  const [resultViewRun, setResultViewRun] = useState(null) // { assessmentId, assessmentTitle, completedRunId, mode: 'view'|'edit' } when showing result view
  const [answersViewRun, setAnswersViewRun] = useState(null) // { run, assessmentSpec } when showing answers view
  const [answerSheetRunId, setAnswerSheetRunId] = useState(null) // runId for answer-sheet modal (completed runs)
  const [completedActionsAnchorEl, setCompletedActionsAnchorEl] = useState(null)
  const [completedActionsAssessment, setCompletedActionsAssessment] = useState(null)
  const [includeNonRunnable, setIncludeNonRunnable] = useState(false)
  const [includeSelfConducted, setIncludeSelfConducted] = useState(false)
  /** When set, show consent screen before proceeding; cleared on accept or cancel. */
  const [consentPending, setConsentPending] = useState(null)
  const [consentChecked, setConsentChecked] = useState(false)
  const runnerActiveRef = useRef(false)
  const fetchRunContextAndSetRunnerRef = useRef(null)
  const resultViewNeedsReloadRef = useRef(false)
  const showApiDebugModeUrlUi = useShowApiDebugModeUrlUi()
  const assessmentRunnerBookedEventInfo = useMemo(
    () => normalizeBookedEventInfoForAssessment(bookedEventInfo),
    [bookedEventInfo],
  )

  useEffect(() => {
    runnerActiveRef.current = Boolean(selectedRun)
  }, [selectedRun])

  // Fire booked-event start log when assessment runner opens from drawer/page.
  useEffect(() => {
    if (!selectedRun) return
    const assessmentEventId = isAssessmentEventPage
      ? bookedEventId
      : sanitizeAssessmentEventBookedEventId(selectedRun?.assessmentEventBookedEventId, bookedEventId)
    if (!assessmentEventId) return
    let cancelled = false
    ;(async () => {
      const startRes = await markBookedEventAsStarted(assessmentEventId)
      if (cancelled) return
      if (!startRes?.success && typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        console.warn('[PractitionerAssessment] failed to log assessment start', {
          assessmentEventId,
          code: startRes?.code,
          message: startRes?.message,
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedRun, bookedEventId, isAssessmentEventPage])

  const openCompletedActionsMenu = useCallback((event, assessment) => {
    setCompletedActionsAnchorEl(event.currentTarget)
    setCompletedActionsAssessment(assessment)
  }, [])

  const closeCompletedActionsMenu = useCallback(() => {
    setCompletedActionsAnchorEl(null)
    setCompletedActionsAssessment(null)
  }, [])

  const resolveAssessmentEventBookedEventId = useCallback((assessmentId, clinicalAssessmentId) => {
    if (!assessmentId || !bookedEventId) return null
    if (isAssessmentEventPage) return bookedEventId
    const fromMap = sanitizeAssessmentEventBookedEventId(
      getMappedAssessmentEventId(bookedEventId, assessmentId),
      bookedEventId,
    )
    if (fromMap) return fromMap
    const fromInSteps = findAssessmentEventIdInParentInSteps(bookedEventInfo, bookedEventId, assessmentId, clinicalAssessmentId)
    if (fromInSteps) {
      setMappedAssessmentEventId(bookedEventId, assessmentId, fromInSteps, clinicalAssessmentId)
      return fromInSteps
    }
    return null
  }, [bookedEventId, bookedEventInfo, isAssessmentEventPage])

  const loadData = useCallback(async () => {
    if (!bookedEventId) return null
    setLoading(true)
    try {
      const hasInitialEventData = Array.isArray(initialEventAssessments)
      const hasInitialRuns = Array.isArray(initialAssessmentRuns)
      const eventParam = isAssessmentEventPage
        ? { bookedEventId }
        : { parentBookedEventId: bookedEventId }
      const runsParams = isAssessmentEventPage
        ? { bookedEventId, includeDrafts: true }
        : { parentBookedEventId: bookedEventId, includeDrafts: true }
      const promises = [
        getPractitionerAssessmentAccessDetails(),
        hasInitialEventData ? Promise.resolve({ eventAssessments: [] }) : getEventAssessments(eventParam, includeNonRunnable, includeSelfConducted),
        hasInitialEventData ? Promise.resolve({ eventAssessments: [] }) : getAvailableAssessments(eventParam, includeNonRunnable, includeSelfConducted),
        getAssessmentRuns(runsParams),
      ]
      const [detailsRes, eventFromApi, availableFromApi, runsFromApi] = await Promise.all(promises)
      setAccessAllowed(detailsRes?.allowedToAssess ?? false)
      const allowed = getAllowedAssessments(detailsRes?.assessmentLists ?? [])
      setAllowedAssessments(allowed)
      const evList = hasInitialEventData ? initialEventAssessments : (eventFromApi?.eventAssessments ?? [])
      const availList = hasInitialEventData ? [] : (availableFromApi?.eventAssessments ?? [])
      setEventAssessments(evList)
      setAvailableAssessments(availList)
      const hasEventAssessments = Array.isArray(evList) && evList.length > 0
      const hasAvailableAssessments = Array.isArray(availList) && availList.length > 0
      setBookingAvailability({
        appointmentConfigured: hasEventAssessments || !hasAvailableAssessments,
        reason: !hasEventAssessments && hasAvailableAssessments ? 'clinical assessment not specified' : null,
      })
      const assessmentIdsByClinicalId = buildAssessmentIdsByClinicalId(evList, availList)
      const assessmentIdsByTitle = buildAssessmentIdsByTitle(evList, availList)
      const runsFromApiList = runsFromApi?.assessmentRuns ?? []
      const resolvedRuns = mergeRunsByAssessmentId(
        hasInitialRuns ? initialAssessmentRuns : [],
        runsFromApiList,
        assessmentIdsByClinicalId,
        assessmentIdsByTitle,
      )
      const resolvedStatuses = (runsFromApi?.assessmentStatuses ?? [])
        .map((statusItem) => ({
          ...statusItem,
          assessmentId: resolveStatusAssessmentIdWithFallback(
            statusItem,
            assessmentIdsByClinicalId,
            assessmentIdsByTitle,
          ),
          status: normalizeAssessmentStatusValue(statusItem?.status),
        }))
        .filter((statusItem) => Boolean(statusItem?.assessmentId))

      const completedIds = new Set(
        (resolvedStatuses ?? [])
          .filter((statusItem) => hasCompletedRunInStatus(statusItem) || statusItem?.status === 'completed')
          .map((statusItem) => statusItem.assessmentId)
          .filter(Boolean),
      )
      ;(resolvedRuns ?? [])
        .filter((run) => isRunCompleted(run))
        .map((run) => resolveRunAssessmentIdWithFallback(run, assessmentIdsByClinicalId, assessmentIdsByTitle))
        .filter(Boolean)
        .forEach((assessmentId) => completedIds.add(assessmentId))

      setAssessmentStatuses(resolvedStatuses)
      setAssessmentRuns((prev) =>
        mergeRunsByAssessmentId(prev, resolvedRuns, assessmentIdsByClinicalId, assessmentIdsByTitle),
      )
      setAssessmentRunsReport(
        runsFromApi?.assessmentReport ?? assessmentReport ?? null,
      )
      if (!isAssessmentEventPage) {
        mergeMappedAssessmentEventsFromStatuses(
          bookedEventId,
          resolvedStatuses,
          assessmentIdsByClinicalId,
          assessmentIdsByTitle,
        )
        mergeMappedAssessmentEventsFromRuns(
          bookedEventId,
          resolvedRuns,
          assessmentIdsByClinicalId,
          assessmentIdsByTitle,
        )
      }
      // Restore in-progress from sessionStorage only when not already in a run (avoid overwriting state)
      if (!runnerActiveRef.current) {
        try {
          const keys = Object.keys(sessionStorage).filter((k) => k.startsWith(DRAFT_KEY_PREFIX) && k.includes(bookedEventId))
          for (const key of keys) {
            const raw = sessionStorage.getItem(key)
            if (!raw) continue
            const draft = JSON.parse(raw)
            if (draft?.assessmentId) {
              if (completedIds.has(draft.assessmentId)) {
                sessionStorage.removeItem(key)
                continue
              }
              const found = findEventAssessment(evList, draft.assessmentId) || findEventAssessment(availList, draft.assessmentId)
              if (!isAssessmentEventPage) {
                const mappedFromDraft = sanitizeAssessmentEventBookedEventId(
                  draft?.assessmentEventBookedEventId,
                  bookedEventId,
                )
                if (mappedFromDraft) {
                  setMappedAssessmentEventId(
                    bookedEventId,
                    draft.assessmentId,
                    mappedFromDraft,
                    draft?.clinicalAssessmentId ?? draft?.psychAssessmentId ?? found?.clinicalAssessment?.id ?? null,
                  )
                }
              }
              if (found) {
                setResumedRun({
                  ...found,
                  draft,
                  assessmentEventBookedEventId: isAssessmentEventPage
                    ? bookedEventId
                    : sanitizeAssessmentEventBookedEventId(draft?.assessmentEventBookedEventId, bookedEventId),
                })
                break
              }
            }
          }
        } catch (e) {
          setResumedRun(null)
        }
      }
      return {
        eventAssessments: evList,
        availableAssessments: availList,
        assessmentRuns: resolvedRuns,
        assessmentStatuses: resolvedStatuses,
      }
    } catch (e) {
      setAllowedAssessments([])
      setEventAssessments([])
      setAvailableAssessments([])
      setAssessmentRuns([])
      setAssessmentStatuses([])
      setAssessmentRunsReport(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [bookedEventId, includeNonRunnable, includeSelfConducted, isAssessmentEventPage, initialEventAssessments, initialAssessmentRuns, assessmentReport])

  // Prefetch when session has bookedEventId (idle so we don't block main page)
  useEffect(() => {
    if (!bookedEventId) return
    const run = () => loadData()
    const id =
      typeof requestIdleCallback !== 'undefined'
        ? requestIdleCallback(run, { timeout: 2800 })
        : setTimeout(run, 400)
    return () =>
      typeof cancelIdleCallback !== 'undefined' ? cancelIdleCallback(id) : clearTimeout(id)
  }, [bookedEventId, loadData])

  useEffect(() => {
  }, [open, bookedEventId])

  // When drawer opens and we still have no data, load immediately so user sees skeleton then data
  useEffect(() => {
    if (open && bookedEventId && !loading && eventAssessments.length === 0 && allowedAssessments.length === 0) {
      loadData()
    }
  }, [open, bookedEventId, loading, eventAssessments.length, allowedAssessments.length, loadData])

  const apiList = getDrawerListFromApi(eventAssessments, availableAssessments)
  // When event-scoped (assessment report page with pre-loaded event assessments), show only assessments configured for this event
  const eventScopedOnly = Boolean(
    Array.isArray(initialEventAssessments) && initialEventAssessments.length > 0 && isAssessmentEventPage,
  )
  const drawerList = eventScopedOnly ? apiList : getDrawerAssessmentList(allowedAssessments, apiList)
  const assessmentIdsByClinicalId = useMemo(
    () => buildAssessmentIdsByClinicalId(eventAssessments, availableAssessments),
    [eventAssessments, availableAssessments],
  )
  const assessmentIdsByTitle = useMemo(
    () => buildAssessmentIdsByTitle(eventAssessments, availableAssessments),
    [eventAssessments, availableAssessments],
  )
  const runByAssessmentId = useMemo(() => {
    const map = new Map()
    ;(assessmentRuns ?? []).forEach((run) => {
      const normalizedRun = normalizeRun(run, null, null, assessmentIdsByClinicalId, assessmentIdsByTitle)
      const assessmentId = resolveRunAssessmentIdWithFallback(
        normalizedRun,
        assessmentIdsByClinicalId,
        assessmentIdsByTitle,
      )
      if (!assessmentId) return
      const existing = map.get(assessmentId) ?? null
      map.set(assessmentId, pickPreferredRun(existing, normalizedRun))
    })
    return map
  }, [assessmentRuns, assessmentIdsByClinicalId, assessmentIdsByTitle])
  const assessmentStatusByAssessmentId = useMemo(() => {
    const map = new Map()
    ;(assessmentStatuses ?? []).forEach((statusItem) => {
      const assessmentId = resolveStatusAssessmentIdWithFallback(
        statusItem,
        assessmentIdsByClinicalId,
        assessmentIdsByTitle,
      )
      if (!assessmentId) return
      const normalizedItem = {
        ...statusItem,
        assessmentId,
        status: normalizeAssessmentStatusValue(statusItem?.status),
      }
      const existing = map.get(assessmentId)
      if (!existing) {
        map.set(assessmentId, normalizedItem)
        return
      }
      const existingCompleted = hasCompletedRunInStatus(existing)
      const nextCompleted = hasCompletedRunInStatus(normalizedItem)
      if (existingCompleted !== nextCompleted) {
        map.set(assessmentId, nextCompleted ? normalizedItem : existing)
        return
      }
      const existingDraft = hasDraftRunInStatus(existing)
      const nextDraft = hasDraftRunInStatus(normalizedItem)
      if (existingDraft !== nextDraft) {
        map.set(assessmentId, nextDraft ? normalizedItem : existing)
        return
      }
      map.set(assessmentId, normalizedItem)
    })
    return map
  }, [assessmentStatuses, assessmentIdsByClinicalId, assessmentIdsByTitle])
  const completedRunIdHintsByAssessmentId = useMemo(() => {
    const map = new Map()
    for (const item of drawerList) {
      const assessmentId = item?.id
      if (!assessmentId) continue
      const statusItem = assessmentStatusByAssessmentId.get(assessmentId)
      const statusRunId =
        statusItem?.latestCompletedRunId ??
        statusItem?.completedRunId ??
        (Array.isArray(statusItem?.completedRunIds) && statusItem.completedRunIds.length > 0
          ? statusItem.completedRunIds[0]
          : null) ??
        null
      if (statusRunId) {
        map.set(assessmentId, statusRunId)
        continue
      }
      const run = runByAssessmentId.get(assessmentId)
      if (run && isRunCompleted(run)) {
        const completedRunId = run?.assessmentRunId ?? run?.id ?? null
        if (completedRunId) {
          map.set(assessmentId, completedRunId)
          continue
        }
      }
      const runIdHint = getAssessmentSpecCompletedRunId(item?.found?.assessmentSpec)
      if (runIdHint) map.set(assessmentId, runIdHint)
    }
    return map
  }, [drawerList, assessmentStatusByAssessmentId, runByAssessmentId])
  const completedIds = useMemo(() => {
    const set = new Set()
    assessmentStatusByAssessmentId.forEach((statusItem, assessmentId) => {
      if (hasCompletedRunInStatus(statusItem) || statusItem?.status === 'completed') {
        set.add(assessmentId)
      }
    })
    runByAssessmentId.forEach((run, assessmentId) => {
      if (isRunCompleted(run)) set.add(assessmentId)
    })
    for (const item of drawerList) {
      if (item?.id && isAssessmentSpecCompleted(item?.found?.assessmentSpec)) {
        set.add(item.id)
      }
    }
    return set
  }, [drawerList, assessmentStatusByAssessmentId, runByAssessmentId])

  // Assessment ids that have a draft in sessionStorage (in progress)
  const inProgressIds = useMemo(() => {
    if (!bookedEventId || !drawerList.length) return new Set()
    const set = new Set()
    assessmentStatusByAssessmentId.forEach((statusItem, assessmentId) => {
      if (hasDraftRunInStatus(statusItem)) set.add(assessmentId)
    })
    runByAssessmentId.forEach((run, assessmentId) => {
      const status = getRunStatus(run)
      if (status === 'started' || status === 'in-progress' || status === 'restarted') {
        set.add(assessmentId)
      }
    })
    try {
      drawerList.forEach((a) => {
        const key = getDraftKey(bookedEventId, a.id)
        if (!sessionStorage.getItem(key)) return
        if (completedIds.has(a.id)) {
          sessionStorage.removeItem(key)
          return
        }
        set.add(a.id)
      })
    } catch (e) {}
    return set
  }, [bookedEventId, drawerList, completedIds, assessmentStatusByAssessmentId, runByAssessmentId])
  const resumableIds = useMemo(() => {
    const set = new Set()
    inProgressIds.forEach((assessmentId) => {
      if (!completedIds.has(assessmentId)) set.add(assessmentId)
    })
    return set
  }, [inProgressIds, completedIds])

  useEffect(() => {
    if (!resumedRun?.assessmentSpec?.assessmentId) return
    if (completedIds.has(resumedRun.assessmentSpec.assessmentId)) {
      setResumedRun(null)
    }
  }, [resumedRun, completedIds])

  // Order: completed/in-progress pinned on top, then startable, then rest.
  const sortedDrawerList = useMemo(() => {
    return [...drawerList].sort((a, b) => {
      const aCompleted = completedIds.has(a.id)
      const bCompleted = completedIds.has(b.id)
      const aInProgress = inProgressIds.has(a.id)
      const bInProgress = inProgressIds.has(b.id)
      const aPinned = aCompleted || aInProgress
      const bPinned = bCompleted || bInProgress
      if (aPinned && !bPinned) return -1
      if (!aPinned && bPinned) return 1
      if (aCompleted && !bCompleted) return -1
      if (!aCompleted && bCompleted) return 1
      if (aInProgress && !bInProgress) return -1
      if (!aInProgress && bInProgress) return 1
      const aCanRun = a.found && !completedIds.has(a.id) && a.found.appointmentConfigured !== false && a.found.runnable !== false
      const bCanRun = b.found && !completedIds.has(b.id) && b.found.appointmentConfigured !== false && b.found.runnable !== false
      if (aCanRun && !bCanRun) return -1
      if (!aCanRun && bCanRun) return 1
      return 0
    })
  }, [drawerList, inProgressIds, completedIds])

  // Group by clinical assessment for sectioned list (title from first item's clinical)
  const drawerListBySection = useMemo(() => {
    const byClinical = new Map()
    for (const a of sortedDrawerList) {
      const cid = a.clinicalAssessmentId ?? a.found?.clinicalAssessment?.id ?? 'other'
      if (!byClinical.has(cid)) {
        byClinical.set(cid, {
          clinicalId: cid,
          title: a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title ?? 'Assessments',
          items: [],
        })
      }
      byClinical.get(cid).items.push(a)
    }
    return Array.from(byClinical.values())
  }, [sortedDrawerList])

  // Per practitioner-assessment-session-ui-updated.md and practitioner-assessment-runs-client.md:
  // Use per-item appointmentConfigured from /available or /event (includePricing=true), not the optional
  // booking-availability endpoint. "You can skip this call if you already request pricing via includePricing=true."

  const handleSelectAssessment = async (assessment) => {
    const found =
      assessment.found ??
      (findEventAssessment(eventAssessments, assessment.id) ||
        findEventAssessment(availableAssessments, assessment.id))
    if (!found) {
      toast.error('This assessment is not configured for this event type.')
      return
    }
    const clinical = found.clinicalAssessment ?? found.psychAssessment
    const { assessmentSpec } = found
    const configured = found.appointmentConfigured !== false
    if (!configured) {
      toast.error(found.reason ?? 'Assessment appointment type not configured for this clinic.')
      return
    }
    const isBundle = found.isBundle === true
    const isPaid = !assessmentSpec?.complimentary && (assessmentSpec?.price != null && assessmentSpec?.price > 0 || (found.appointmentPrice != null && found.appointmentPrice > 0))
    const feeAmount = found.appointmentPrice ?? assessmentSpec?.price
    const inProgress = resumableIds.has(assessmentSpec?.assessmentId ?? assessment?.id)

    const title = assessmentSpec?.title ?? clinical?.title ?? assessment?.title ?? 'Assessment'
    const assessmentIdForConsent = assessmentSpec?.assessmentId ?? assessment?.id
    // If consent was already given (e.g. from inline launcher for single-test), skip consent and go straight to runner
    if (isAssessmentEventPage && assessmentIdForConsent && hasStoredConsent(bookedEventId, assessmentIdForConsent)) {
      await doBookingAndOpenRunner({ assessment, found, clinical, assessmentSpec, isBundle, isPaid, feeAmount, isInProgress: inProgress })
      return
    }
    setConsentChecked(false)
    setConsentPending({
      type: 'fromList',
      title,
      assessmentId: assessmentIdForConsent,
      payload: { assessment, found, clinical, assessmentSpec, isBundle, isPaid, feeAmount, isInProgress: inProgress },
    })
    return
  }

  /** Run booking and open runner; called after consent for Start from list. */
  const doBookingAndOpenRunner = useCallback(async (payload) => {
    const { assessment, found, clinical, assessmentSpec, isBundle, isPaid, feeAmount } = payload
    setBookingInProgress(true)
    setPaymentPending(null)
    setLoadingAssessmentDisplay({
      title: assessmentSpec?.title ?? clinical?.title ?? assessment?.title ?? 'Assessment',
      description: assessmentSpec?.description ?? clinical?.description,
      icon: assessmentSpec?.icon ?? clinical?.icon,
      coverIcon: assessmentSpec?.coverIcon ?? clinical?.coverIcon,
    })
    try {
      let availabilityRes = null
      try {
        availabilityRes = await getBookingAvailability({ parentBookedEventId: bookedEventId }, clinical.id)
      } catch (e) {
        availabilityRes = null
      }
      if (availabilityRes) {
        const availabilityCode = availabilityRes?.code
        if (availabilityCode === 4030 || availabilityCode === 4034) {
          toast.error(getAssessmentBookingErrorMessage(availabilityCode, availabilityRes?.message))
          return
        }
        if (availabilityRes?.appointmentConfigured === false) {
          toast.error(availabilityRes?.reason || 'Assessment appointment type not configured for this clinic.')
          return
        }
      }
      const bookPayload = {
        parentBookedEventId: bookedEventId,
        clinicalAssessmentId: clinical.id,
        bookingType: isPaid ? 'paid' : 'free',
        sendPaymentLinkToPatient: isPaid,
        sendPaymentLinkToPatientRelative: false,
        phoneNumbersToSendThePaymentLink: [],
      }
      if (isPaid && feeAmount != null && feeAmount > 0) bookPayload.feeAmount = Number(feeAmount)
      if (!isBundle) bookPayload.assessmentId = assessmentSpec.assessmentId
      // Per practitioner-assessment-book-start-time.md: includeAuxData=true returns assessment detail in response so we skip a separate detail API call.
      if (!isBundle) bookPayload.includeAuxData = true
      bookPayload.languageCode = getMainLanguageCode() || 'en'
      // startTime: avoid overlapping with series. If parent has inSteps with a valid refId different from parent id, use endTime of the latest step in inSteps; else if current time is within parent [startTime, endTime] use current time; else use parent endTime.
      const parentId = bookedEventInfo?.id
      const inSteps = Array.isArray(bookedEventInfo?.inSteps) ? bookedEventInfo.inSteps : []
      const hasOtherRefInSeries = inSteps.some((step) => step?.refId != null && step?.refId !== parentId)
      let resolvedStartTime = null
      if (hasOtherRefInSeries && inSteps.length > 0) {
        const stepsWithEndTime = inSteps.filter((s) => s?.endTime != null)
        if (stepsWithEndTime.length > 0) {
          const latest = stepsWithEndTime.reduce((a, b) =>
            dayjs.utc(a.endTime).isAfter(dayjs.utc(b.endTime)) ? a : b,
          )
          const endMoment = dayjs.utc(latest.endTime)
          if (endMoment.isValid()) resolvedStartTime = endMoment.toISOString()
        }
      }
      if (resolvedStartTime == null && bookedEventInfo?.startTime != null && bookedEventInfo?.endTime != null) {
        const now = dayjs.utc()
        const parentStart = dayjs.utc(bookedEventInfo.startTime)
        const parentEnd = dayjs.utc(bookedEventInfo.endTime)
        if (parentStart.isValid() && parentEnd.isValid()) {
          const isNowDuringSession = !now.isBefore(parentStart) && !now.isAfter(parentEnd)
          resolvedStartTime = (isNowDuringSession ? now : parentEnd).toISOString()
        }
      }
      if (resolvedStartTime) bookPayload.startTime = resolvedStartTime
      const res = await bookAssessment(bookPayload)
      if (res?.status === 200 && isCommonResponseSuccessful(res?.data?.code)) {
        const data = res?.data?.data ?? res?.data
        const customProtocolRuntime =
          isCustomProtocolAssessmentSpec(assessmentSpec) ||
          isCustomProtocolRunnerType(data?.runnerType)
        if (isPaid && data?.assessmentBookedEvent?.paymentStatus === 'pending') {
          setBookingInProgress(false)
          setLoadingAssessmentDisplay(null)
          setPaymentPending({ assessmentId: assessmentSpec.assessmentId, assessmentTitle: assessmentSpec.title })
          toast.info('Payment link sent to patient. Use Refresh after payment.')
          return
        }
        setPaymentPending(null)
        setResumedRun(null)
        const assessmentEventIdFromBooking = extractAssessmentEventBookedEventIdFromBookingData(data, bookedEventId)
        const resolvedAssessmentEventId =
          isAssessmentEventPage
            ? bookedEventId
            : assessmentEventIdFromBooking ?? resolveAssessmentEventBookedEventId(assessmentSpec.assessmentId, clinical?.id)
        if (!isAssessmentEventPage) {
          if (resolvedAssessmentEventId) {
            setMappedAssessmentEventId(bookedEventId, assessmentSpec.assessmentId, resolvedAssessmentEventId, clinical?.id)
          } else {
            toast.error('Assessment booking id missing. Please refresh and try again.')
            loadData()
            return
          }
        }
        if (customProtocolRuntime) {
          const ok = typeof fetchRunContextAndSetRunnerRef.current === 'function'
            ? await fetchRunContextAndSetRunnerRef.current(clinical, {
              ...assessmentSpec,
              runnerType: 'CUSTOM_PROTOCOL',
            })
            : false
          if (!ok) {
            setSelectedRun({
              clinicalAssessment: clinical,
              psychAssessment: clinical,
              assessmentSpec: {
                ...assessmentSpec,
                runnerType: 'CUSTOM_PROTOCOL',
              },
              appointmentConfigured: found?.appointmentConfigured,
              runnable: found?.runnable,
              assessmentEventBookedEventId: resolvedAssessmentEventId,
            })
          }
          loadData()
          return
        }
        // Per practitioner-assessment-book-start-time.md: when includeAuxData=true, booking response includes assessment detail; use it to avoid a separate detail API call.
        const inlineAssessment = data?.assessment ?? data?.assessmentDetail?.assessment ?? data?.assessmentDetail
        const inlineQnaUnlocked = data?.qnaUnlocked
        let spec
        if (inlineAssessment && typeof inlineQnaUnlocked !== 'undefined') {
          const lang = getMainLanguageCode() || 'en'
          const resolvedJournalQusAns =
            inlineAssessment?.translations?.[lang]?.journalQusAns ?? inlineAssessment?.journalQusAns
          spec = {
            ...inlineAssessment,
            assessmentId: inlineAssessment?.id ?? assessmentSpec.assessmentId,
            qnaUnlocked: inlineQnaUnlocked,
            qnaLockedReason: data?.qnaLockedReason,
            journalQusAns: Array.isArray(resolvedJournalQusAns) ? resolvedJournalQusAns : inlineAssessment?.journalQusAns,
          }
        } else {
          const fromData = extractAssessmentEventBookedEventIdFromBookingData(data, bookedEventId)
          const detailBookedEventId =
            isAssessmentEventPage
              ? bookedEventId
              : (fromData ?? resolveAssessmentEventBookedEventId(assessmentSpec.assessmentId, clinical?.id))
          let detailAssessment = null
          if (detailBookedEventId) {
            const result = await getAssessmentDetail(assessmentSpec.assessmentId, detailBookedEventId)
            detailAssessment = result?.assessment
          }
          if (detailAssessment) {
            const lang = getMainLanguageCode() || 'en'
            const resolvedJournalQusAns =
              detailAssessment?.translations?.[lang]?.journalQusAns ?? detailAssessment?.journalQusAns
            spec = {
              ...detailAssessment,
              assessmentId: detailAssessment?.id ?? assessmentSpec.assessmentId,
              journalQusAns: Array.isArray(resolvedJournalQusAns) ? resolvedJournalQusAns : detailAssessment?.journalQusAns,
            }
          } else {
            spec = assessmentSpec
          }
        }
        if (spec?.qnaUnlocked === true) {
          setSelectedRun({
            clinicalAssessment: clinical,
            psychAssessment: clinical,
            assessmentSpec: spec,
            appointmentConfigured: found?.appointmentConfigured,
            runnable: found?.runnable,
            assessmentEventBookedEventId: resolvedAssessmentEventId,
          })
        } else {
          const reason = spec?.qnaLockedReason || 'Book/complete payment for this assessment to unlock questions.'
          toast.info(reason)
        }
        loadData()
        return
      }
      const code = res?.data?.code
      const msg = getAssessmentBookingErrorMessage(code, res?.data?.message)
      toast.error(msg)
    } catch (e) {
      toast.error('Failed to book assessment')
    } finally {
      setBookingInProgress(false)
      setLoadingAssessmentDisplay(null)
    }
  }, [bookedEventId, bookedEventInfo, isAssessmentEventPage, loadData, resolveAssessmentEventBookedEventId])

  const handleRefreshAfterPayment = useCallback(() => {
    setPaymentPending(null)
    loadData()
    onRunSubmitted?.()
  }, [loadData, onRunSubmitted])

  const handleRunSuccess = useCallback((submitResultPayload) => {
    const fallbackAssessmentId = selectedRun?.assessmentSpec?.assessmentId
    const fallbackAssessmentTitle = selectedRun?.assessmentSpec?.title
    const { run, report } = extractRunAndReport(
      submitResultPayload,
      fallbackAssessmentId,
      fallbackAssessmentTitle,
      assessmentIdsByClinicalId,
      assessmentIdsByTitle,
    )
    const normalizedRun = normalizeRun(
      run,
      fallbackAssessmentId,
      fallbackAssessmentTitle,
      assessmentIdsByClinicalId,
      assessmentIdsByTitle,
    )
    if (normalizedRun?.assessmentId) {
      const optimisticAssessmentEventId = isAssessmentEventPage
        ? bookedEventId
        : sanitizeAssessmentEventBookedEventId(
          normalizedRun?.assessmentEventBookedEventId ??
            normalizedRun?.assessmentBookedEventId ??
            normalizedRun?.bookedEventId ??
            normalizedRun?.assessmentBookedEvent?.id ??
            selectedRun?.assessmentEventBookedEventId,
          bookedEventId,
        )
      const optimisticRun = {
        ...normalizedRun,
        assessmentEventBookedEventId: optimisticAssessmentEventId,
      }
      setAssessmentRuns((prev) =>
        mergeRunsByAssessmentId(prev, [optimisticRun], assessmentIdsByClinicalId, assessmentIdsByTitle),
      )
      if (!isAssessmentEventPage && optimisticAssessmentEventId) {
        const clinicalId =
          normalizedRun?.clinicalAssessmentId ??
          selectedRun?.clinicalAssessment?.id ??
          selectedRun?.psychAssessment?.id ??
          null
        setMappedAssessmentEventId(
          bookedEventId,
          normalizedRun.assessmentId,
          optimisticAssessmentEventId,
          clinicalId,
        )
      }
    }
    if (report) setAssessmentRunsReport(report)
    setSelectedRun(null)
    setResumedRun(null)
    try {
      if (bookedEventId) {
        const keys = Object.keys(sessionStorage).filter((k) => k.startsWith(DRAFT_KEY_PREFIX) && k.includes(bookedEventId))
        keys.forEach((k) => sessionStorage.removeItem(k))
      }
    } catch (e) {}
    loadData()
    onRunSubmitted?.()
  }, [
    assessmentIdsByClinicalId,
    assessmentIdsByTitle,
    bookedEventId,
    isAssessmentEventPage,
    loadData,
    onRunSubmitted,
    selectedRun,
  ])

  const handleBackFromRunner = () => {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.warn('[PractitionerAssessment] Back from runner: returning to list (selectedRun cleared)')
    }
    setSelectedRun(null)
    setResumedRun(null)
  }

  /** Fetch runner context (assessment + latestRunAnswers, qnaUnlocked). Prefer over getAssessmentDetail to avoid extra calls. */
  const fetchRunContextAndSetRunner = useCallback(
    async (clinicalOrPsych, assessmentSpec, prefillAnswers = null) => {
      const clinicalId = clinicalOrPsych?.id
      const assessmentId = assessmentSpec?.assessmentId ?? assessmentSpec?.id
      if (!clinicalId || !assessmentId || !bookedEventId) return false
      const customProtocolRequested = isCustomProtocolAssessmentSpec(assessmentSpec)
      try {
        const runContextParams = {
          clinicalAssessmentId: clinicalId,
          languageCode: getMainLanguageCode() || 'en',
        }
        if (!customProtocolRequested || assessmentId) {
          runContextParams.assessmentId = assessmentId
        }
        const mappedAssessmentEventId = resolveAssessmentEventBookedEventId(assessmentId, clinicalId)
        if (isAssessmentEventPage) {
          runContextParams.assessmentEventBookedEventId = bookedEventId
        } else {
          if (!mappedAssessmentEventId) {
            return false
          }
          runContextParams.parentBookedEventId = bookedEventId
          runContextParams.bookedEventId = mappedAssessmentEventId
        }
        const {
          assessment,
          assessmentSpec: runContextAssessmentSpec,
          runnerType,
          customProtocolDescriptor,
          qnaUnlocked,
          qnaLockedReason,
          latestRunAnswers,
          latestRunStructuredResponses,
          latestRunCustomRunData,
          latestDraftAnswers,
          latestDraftStructuredResponses,
          latestDraftCustomRunData,
          latestDraftRunId,
          latestDraftRunRevision,
          latestAssessmentRun,
          latestCompletedRunId,
          latestCompletedAssessmentRun,
          latestCompletedStructuredResponses,
          latestCompletedCustomRunData,
          assessmentCompleted,
          assessmentBookedEventId,
        } = await getRunContext(runContextParams)
        const customProtocolRuntime =
          isCustomProtocolRunnerType(runnerType) ||
          isCustomProtocolAssessmentSpec(runContextAssessmentSpec) ||
          customProtocolRequested
        const resolvedAssessmentEventId = isAssessmentEventPage
          ? bookedEventId
          : (
            sanitizeAssessmentEventBookedEventId(assessmentBookedEventId, bookedEventId) ??
            sanitizeAssessmentEventBookedEventId(latestAssessmentRun?.assessmentEventBookedEventId, bookedEventId) ??
            sanitizeAssessmentEventBookedEventId(latestAssessmentRun?.assessmentBookedEventId, bookedEventId) ??
            sanitizeAssessmentEventBookedEventId(latestAssessmentRun?.bookedEventId, bookedEventId) ??
            sanitizeAssessmentEventBookedEventId(latestAssessmentRun?.run?.bookedEventId, bookedEventId) ??
            mappedAssessmentEventId ??
            null
          )
        if (assessmentCompleted) {
          const completedRunId =
            latestCompletedRunId ??
            latestCompletedAssessmentRun?.id ??
            latestCompletedAssessmentRun?.assessmentRunId ??
            null
          const fallbackCompletedRun =
            latestCompletedAssessmentRun ??
            {
              id: completedRunId,
              assessmentRunId: completedRunId,
              assessmentId,
              clinicalAssessmentId: clinicalId,
              psychAssessmentId: clinicalId,
            }
          const completedRun = normalizeRun(
            fallbackCompletedRun,
            assessmentId,
            assessmentSpec?.title ?? clinicalOrPsych?.title ?? 'Assessment',
            assessmentIdsByClinicalId,
            assessmentIdsByTitle,
          )
          if (completedRun?.assessmentId) {
            const completedWithEvent = {
              ...completedRun,
              assessmentEventBookedEventId: resolvedAssessmentEventId,
            }
            setAssessmentRuns((prev) =>
              mergeRunsByAssessmentId(
                prev,
                [completedWithEvent],
                assessmentIdsByClinicalId,
                assessmentIdsByTitle,
              ),
            )
          }
          try {
            sessionStorage.removeItem(getDraftKey(bookedEventId, assessmentId))
          } catch (e) {}
          if (!isAssessmentEventPage && resolvedAssessmentEventId) {
            setMappedAssessmentEventId(bookedEventId, assessmentId, resolvedAssessmentEventId, clinicalId)
          }
          setSelectedRun(null)
          setResumedRun(null)
          const resolvedCompletedRunId =
            completedRunId ?? completedRun?.assessmentRunId ?? completedRun?.id ?? null
          setResultViewRun({
            assessmentId,
            assessmentTitle: assessmentSpec?.title ?? clinicalOrPsych?.title ?? 'Assessment',
            completedRunId: resolvedCompletedRunId,
            mode: 'view',
            // Force run/result fetch when a completedRunId is available.
            initialRun: resolvedCompletedRunId ? null : (completedRun ?? null),
            initialAssessmentReport: resolvedCompletedRunId ? null : (assessmentRunsReport ?? assessmentReport ?? null),
          })
          return true
        }
        if (!isAssessmentEventPage && resolvedAssessmentEventId) {
          setMappedAssessmentEventId(bookedEventId, assessmentId, resolvedAssessmentEventId, clinicalId)
        }
        if (customProtocolRuntime) {
          if (!isAssessmentEventPage && !resolvedAssessmentEventId) {
            toast.error('Assessment booking id missing. Please refresh and try again.')
            return false
          }
          const customSpec = {
            ...assessmentSpec,
            ...(runContextAssessmentSpec ?? {}),
            assessmentId,
            runnerType: 'CUSTOM_PROTOCOL',
            customProtocol:
              customProtocolDescriptor ??
              runContextAssessmentSpec?.customProtocol ??
              assessmentSpec?.customProtocol ??
              null,
          }
          setSelectedRun({
            clinicalAssessment: clinicalOrPsych,
            psychAssessment: clinicalOrPsych,
            assessmentSpec: customSpec,
            initialStructuredResponses:
              prefillAnswers ??
              latestDraftStructuredResponses ??
              latestRunStructuredResponses ??
              latestCompletedStructuredResponses ??
              {},
            initialCustomRunData:
              latestDraftCustomRunData ??
              latestRunCustomRunData ??
              latestCompletedCustomRunData ??
              {},
            initialDraftRunId: latestDraftRunId ?? null,
            initialDraftRevision: latestDraftRunRevision ?? null,
            latestCompletedRunId: latestCompletedRunId ?? null,
            assessmentEventBookedEventId: resolvedAssessmentEventId,
          })
          return true
        }
        if (!assessment) {
          const detailBookedEventId = resolvedAssessmentEventId ?? mappedAssessmentEventId
          if (!detailBookedEventId && !isAssessmentEventPage) {
            toast.error('Assessment booking id missing. Please refresh and try again.')
            return false
          }
          const { assessment: detailAssessment } = await getAssessmentDetail(assessmentId, detailBookedEventId ?? bookedEventId)
          if (detailAssessment) {
            const lang = getMainLanguageCode() || 'en'
            const jqa = detailAssessment?.translations?.[lang]?.journalQusAns ?? detailAssessment?.journalQusAns
            const spec = { ...assessmentSpec, ...detailAssessment, assessmentId, journalQusAns: Array.isArray(jqa) ? jqa : detailAssessment?.journalQusAns }
            if (!isAssessmentEventPage && !resolvedAssessmentEventId) {
              toast.error('Assessment booking id missing. Please refresh and try again.')
              return false
            }
            setSelectedRun({
              clinicalAssessment: clinicalOrPsych,
              psychAssessment: clinicalOrPsych,
              assessmentSpec: spec,
              initialRunAnswers: prefillAnswers ?? latestDraftAnswers ?? latestRunAnswers ?? {},
              initialDraftRunId: latestDraftRunId ?? null,
              initialDraftRevision: latestDraftRunRevision ?? null,
              latestCompletedRunId: latestCompletedRunId ?? null,
              assessmentEventBookedEventId: resolvedAssessmentEventId,
            })
            return true
          }
          return false
        }
        const lang = getMainLanguageCode() || 'en'
        const jqa = assessment?.translations?.[lang]?.journalQusAns ?? assessment?.journalQusAns
        const spec = { ...assessmentSpec, ...assessment, assessmentId, journalQusAns: Array.isArray(jqa) ? jqa : assessment?.journalQusAns, qnaUnlocked, qnaLockedReason }
        if (qnaUnlocked !== true) {
          toast.info(qnaLockedReason || 'Book/complete payment for this assessment to unlock questions.')
          return false
        }
        if (!isAssessmentEventPage && !resolvedAssessmentEventId) {
          toast.error('Assessment booking id missing. Please refresh and try again.')
          return false
        }
        setSelectedRun({
          clinicalAssessment: clinicalOrPsych,
          psychAssessment: clinicalOrPsych,
          assessmentSpec: spec,
          initialRunAnswers: prefillAnswers ?? latestDraftAnswers ?? latestRunAnswers ?? {},
          initialDraftRunId: latestDraftRunId ?? null,
          initialDraftRevision: latestDraftRunRevision ?? null,
          latestCompletedRunId: latestCompletedRunId ?? null,
          assessmentEventBookedEventId: resolvedAssessmentEventId,
        })
        return true
      } catch (e) {
        return false
      }
    },
    [
      assessmentIdsByClinicalId,
      bookedEventId,
      isAssessmentEventPage,
      resolveAssessmentEventBookedEventId,
      assessmentIdsByTitle,
      assessmentRunsReport,
      assessmentReport,
    ],
  )

  useEffect(() => {
    fetchRunContextAndSetRunnerRef.current = fetchRunContextAndSetRunner
  }, [fetchRunContextAndSetRunner])

  /** Run after consent for Resume / Start fresh. */
  const runAfterConsentResume = useCallback(async (payload) => {
    const { clinicalOrPsych, assessmentSpec } = payload
    setResumedRun(null)
    setStartingRunner(true)
    setLoadingAssessmentDisplay({
      title: assessmentSpec?.title ?? clinicalOrPsych?.title ?? 'Assessment',
      description: assessmentSpec?.description ?? clinicalOrPsych?.description,
      icon: assessmentSpec?.icon ?? clinicalOrPsych?.icon,
      coverIcon: assessmentSpec?.coverIcon ?? clinicalOrPsych?.coverIcon,
    })
    try {
      const ok = await fetchRunContextAndSetRunner(clinicalOrPsych, assessmentSpec)
      if (!ok) {
        const assessmentId = assessmentSpec?.assessmentId ?? assessmentSpec?.id
        const mappedAssessmentEventId = resolveAssessmentEventBookedEventId(assessmentId, clinicalOrPsych?.id)
        if (isCustomProtocolAssessmentSpec(assessmentSpec)) {
          if (!isAssessmentEventPage && !mappedAssessmentEventId) {
            toast.error('Assessment booking id missing. Please refresh and try again.')
            return
          }
          setSelectedRun({
            clinicalAssessment: clinicalOrPsych,
            psychAssessment: clinicalOrPsych,
            assessmentSpec: {
              ...assessmentSpec,
              runnerType: 'CUSTOM_PROTOCOL',
            },
            assessmentEventBookedEventId: isAssessmentEventPage ? bookedEventId : mappedAssessmentEventId,
          })
          return
        }
        if (!isAssessmentEventPage && !mappedAssessmentEventId) {
          toast.error('Assessment booking id missing. Please refresh and try again.')
          return
        }
        const detailBookedEventId = isAssessmentEventPage ? bookedEventId : mappedAssessmentEventId
        const { assessment: detailAssessment } = await getAssessmentDetail(assessmentId, detailBookedEventId)
        if (detailAssessment) {
          const lang = getMainLanguageCode() || 'en'
          const jqa = detailAssessment?.translations?.[lang]?.journalQusAns ?? detailAssessment?.journalQusAns
          const spec = { ...assessmentSpec, ...detailAssessment, assessmentId, journalQusAns: Array.isArray(jqa) ? jqa : detailAssessment?.journalQusAns }
          setSelectedRun({
            clinicalAssessment: clinicalOrPsych,
            psychAssessment: clinicalOrPsych,
            assessmentSpec: spec,
            assessmentEventBookedEventId: isAssessmentEventPage ? bookedEventId : mappedAssessmentEventId,
          })
        }
      }
    } finally {
      setStartingRunner(false)
      setLoadingAssessmentDisplay(null)
    }
  }, [bookedEventId, fetchRunContextAndSetRunner, isAssessmentEventPage, resolveAssessmentEventBookedEventId])

  /** Run after consent for Restart this assessment. */
  const runAfterConsentRestart = useCallback(async (payload) => {
    const { clinical, assessmentSpec } = payload
    setResumedRun(null)
    setStartingRunner(true)
    setLoadingAssessmentDisplay({
      title: assessmentSpec?.title ?? clinical?.title ?? 'Assessment',
      description: assessmentSpec?.description ?? clinical?.description,
      icon: assessmentSpec?.icon ?? clinical?.icon,
      coverIcon: assessmentSpec?.coverIcon ?? clinical?.coverIcon,
    })
    try {
      const ok = await fetchRunContextAndSetRunner(clinical, assessmentSpec)
      if (!ok) {
        const assessmentId = assessmentSpec?.assessmentId ?? assessmentSpec?.id
        const mappedAssessmentEventId = resolveAssessmentEventBookedEventId(assessmentId, clinical?.id)
        if (isCustomProtocolAssessmentSpec(assessmentSpec)) {
          if (!isAssessmentEventPage && !mappedAssessmentEventId) {
            toast.error('Assessment booking id missing. Please refresh and try again.')
            return
          }
          setSelectedRun({
            clinicalAssessment: clinical,
            psychAssessment: clinical,
            assessmentSpec: {
              ...assessmentSpec,
              runnerType: 'CUSTOM_PROTOCOL',
            },
            assessmentEventBookedEventId: isAssessmentEventPage ? bookedEventId : mappedAssessmentEventId,
          })
          return
        }
        if (!isAssessmentEventPage && !mappedAssessmentEventId) {
          toast.error('Assessment booking id missing. Please refresh and try again.')
          return
        }
        const detailBookedEventId = isAssessmentEventPage ? bookedEventId : mappedAssessmentEventId
        const { assessment: detailAssessment } = await getAssessmentDetail(assessmentId, detailBookedEventId)
        if (detailAssessment) {
          const lang = getMainLanguageCode() || 'en'
          const jqa = detailAssessment?.translations?.[lang]?.journalQusAns ?? detailAssessment?.journalQusAns
          const spec = { ...assessmentSpec, ...detailAssessment, assessmentId, journalQusAns: Array.isArray(jqa) ? jqa : detailAssessment?.journalQusAns }
          setSelectedRun({
            clinicalAssessment: clinical,
            psychAssessment: clinical,
            assessmentSpec: spec,
            assessmentEventBookedEventId: isAssessmentEventPage ? bookedEventId : mappedAssessmentEventId,
          })
        }
      }
    } finally {
      setStartingRunner(false)
      setLoadingAssessmentDisplay(null)
    }
  }, [bookedEventId, fetchRunContextAndSetRunner, isAssessmentEventPage, resolveAssessmentEventBookedEventId])

  const handleStartRunner = async (clinicalOrPsych, assessmentSpec) => {
    if (!isCustomProtocolAssessmentSpec(assessmentSpec) && assessmentSpec?.qnaUnlocked === false) {
      const reason = assessmentSpec?.qnaLockedReason || 'Book/complete payment for this assessment to unlock questions.'
      toast.info(reason)
      return
    }
    const title = assessmentSpec?.title ?? clinicalOrPsych?.title ?? 'Assessment'
    setConsentChecked(false)
    setConsentPending({
      type: 'resume',
      title,
      assessmentId: assessmentSpec?.assessmentId ?? assessmentSpec?.id,
      payload: { clinicalOrPsych, assessmentSpec },
    })
  }

  /** Redo this assessment: show consent then clear draft and open runner with latest answers. */
  const handleRestartThisAssessment = useCallback((assessmentToRedo) => {
    if (!assessmentToRedo || !bookedEventId) return
    const found =
      assessmentToRedo.found ??
      findEventAssessment(eventAssessments, assessmentToRedo.id) ??
      findEventAssessment(availableAssessments, assessmentToRedo.id)
    if (!isCustomProtocolAssessmentSpec(found?.assessmentSpec) && !found?.assessmentSpec?.qnaUnlocked) {
      toast.info(found?.assessmentSpec?.qnaLockedReason || 'Book/complete payment for this assessment to unlock questions.')
      return
    }
    try {
      sessionStorage.removeItem(getDraftKey(bookedEventId, assessmentToRedo.id))
    } catch (e) {}
    const clinical = found.clinicalAssessment ?? found.psychAssessment
    const assessmentSpec = found.assessmentSpec
    const title = assessmentSpec?.title ?? clinical?.title ?? assessmentToRedo?.title ?? 'Assessment'
    setConsentChecked(false)
    setConsentPending({
      type: 'restart',
      title,
      assessmentId: assessmentSpec?.assessmentId ?? assessmentToRedo?.id,
      payload: { clinical, assessmentSpec },
    })
  }, [bookedEventId, eventAssessments, availableAssessments])

  const showList = !selectedRun && !resumedRun && !resultViewRun && !answersViewRun
  const showPaymentPending = paymentPending && !selectedRun
  const effectiveDrawerWidth = useMemo(() => {
    const numericDrawerWidth = Number(drawerWidth)
    const baseWidth = Number.isFinite(numericDrawerWidth) ? numericDrawerWidth : DEFAULT_DRAWER_WIDTH
    const shouldUseWideDrawer =
      isCustomProtocolAssessmentSpec(selectedRun?.assessmentSpec) ||
      isCustomProtocolRunnerType(resultViewRun?.initialRun?.runnerType ?? resultViewRun?.initialRun?.assessmentSpec?.runnerType)
    return shouldUseWideDrawer ? Math.max(baseWidth, CUSTOM_PROTOCOL_DRAWER_WIDTH) : drawerWidth
  }, [drawerWidth, resultViewRun?.initialRun?.assessmentSpec?.runnerType, resultViewRun?.initialRun?.runnerType, selectedRun?.assessmentSpec])

  const handleBackFromAnswersView = useCallback(() => {
    setAnswersViewRun(null)
  }, [])

  const openAnswerSheetForAssessment = useCallback((assessmentId) => {
    const run = runByAssessmentId.get(assessmentId) ?? null
    if (isCustomProtocolRunnerType(run?.runnerType ?? run?.assessmentSpec?.runnerType)) {
      toast.info('Answer sheet is not available yet for custom protocol runs.')
      return
    }
    const runId = completedRunIdHintsByAssessmentId.get(assessmentId) ?? run?.assessmentRunId ?? run?.id ?? null
    if (runId) setAnswerSheetRunId(runId)
    else toast.error('Run data not found')
  }, [runByAssessmentId, completedRunIdHintsByAssessmentId])

  const handleViewResult = useCallback((assessmentId, assessmentTitle, submitResultPayload = null, forcedCompletedRunId = null) => {
    resultViewNeedsReloadRef.current = false
    const { run: runFromSubmit, report: reportFromSubmit } = extractRunAndReport(
      submitResultPayload,
      assessmentId,
      assessmentTitle,
      assessmentIdsByClinicalId,
      assessmentIdsByTitle,
    )
    const normalizedRunFromSubmit = normalizeRun(
      runFromSubmit,
      assessmentId,
      assessmentTitle,
      assessmentIdsByClinicalId,
      assessmentIdsByTitle,
    )
    const runFromState = runByAssessmentId.get(assessmentId) ?? null
    const initialRun = normalizedRunFromSubmit ?? runFromState
    const completedRunId =
      forcedCompletedRunId ??
      completedRunIdHintsByAssessmentId.get(assessmentId) ??
      initialRun?.assessmentRunId ??
      initialRun?.id ??
      null
    if (normalizedRunFromSubmit?.assessmentId) {
      setAssessmentRuns((prev) =>
        mergeRunsByAssessmentId(
          prev,
          [normalizedRunFromSubmit],
          assessmentIdsByClinicalId,
          assessmentIdsByTitle,
        ),
      )
    }
    if (reportFromSubmit) setAssessmentRunsReport(reportFromSubmit)
    setSelectedRun(null)
    setResumedRun(null)
    setResultViewRun({
      assessmentId,
      assessmentTitle: assessmentTitle ?? initialRun?.assessmentTitle ?? 'Assessment',
      completedRunId,
      mode: 'view',
      initialRun,
      initialAssessmentReport: reportFromSubmit ?? assessmentRunsReport ?? assessmentReport ?? null,
    })
  }, [
    runByAssessmentId,
    completedRunIdHintsByAssessmentId,
    assessmentRunsReport,
    assessmentReport,
    assessmentIdsByClinicalId,
    assessmentIdsByTitle,
  ])

  const handleEditReport = useCallback((assessmentId, assessmentTitle) => {
    resultViewNeedsReloadRef.current = false
    const initialRun = runByAssessmentId.get(assessmentId) ?? null
    setSelectedRun(null)
    setResumedRun(null)
    setResultViewRun({
      assessmentId,
      assessmentTitle: assessmentTitle ?? initialRun?.assessmentTitle ?? 'Assessment',
      completedRunId:
        initialRun?.id ??
        initialRun?.assessmentRunId ??
        completedRunIdHintsByAssessmentId.get(assessmentId) ??
        null,
      mode: 'edit',
      initialRun,
      initialAssessmentReport: assessmentRunsReport ?? assessmentReport ?? null,
    })
  }, [
    runByAssessmentId,
    assessmentRunsReport,
    assessmentReport,
    completedRunIdHintsByAssessmentId,
  ])

  // When used as launcher action drawer: after load, open directly to the requested assessment and action (must run after drawerList and handlers are defined)
  const openWithHandledRef = useRef(false)
  useEffect(() => {
    if (!open || !openWithAssessmentId || loading || drawerList.length === 0) return
    if (openWithHandledRef.current) return
    const item = drawerList.find((a) => a.id === openWithAssessmentId)
    if (!item) {
      onOpenWithHandled?.()
      return
    }
    openWithHandledRef.current = true
    const runId = openWithCompletedRunId ?? completedRunIdHintsByAssessmentId.get(openWithAssessmentId) ?? null
    if (openWithAction === 'start' || openWithAction === 'resume') {
      handleSelectAssessment(item)
    } else if (openWithAction === 'result') {
      handleViewResult(openWithAssessmentId, item.title, null, runId)
    } else if (openWithAction === 'edit') {
      handleEditReport(openWithAssessmentId, item.title)
    } else if (openWithAction === 'answers') {
      openAnswerSheetForAssessment(openWithAssessmentId)
    }
    onOpenWithHandled?.()
  }, [open, openWithAssessmentId, openWithAction, openWithCompletedRunId, loading, drawerList, completedRunIdHintsByAssessmentId, onOpenWithHandled])
  useEffect(() => {
    if (!open) openWithHandledRef.current = false
  }, [open])

  const handleResultViewReportSaved = useCallback(() => {
    resultViewNeedsReloadRef.current = true
    onAssessmentReportSaved?.()
  }, [onAssessmentReportSaved])

  const handleBackFromResultView = useCallback(() => {
    setResultViewRun(null)
    if (resultViewNeedsReloadRef.current) {
      resultViewNeedsReloadRef.current = false
      loadData()
      // Do not call onRunSubmitted here — only refresh drawer content so the page does not reload
      return
    }
    resultViewNeedsReloadRef.current = false
  }, [loadData])

  const handleConsentCancel = useCallback(() => {
    setConsentPending(null)
    setConsentChecked(false)
  }, [])

  const handleRedoFromCompletedMenu = useCallback(() => {
    if (completedActionsAssessment) handleRestartThisAssessment(completedActionsAssessment)
    closeCompletedActionsMenu()
  }, [completedActionsAssessment, closeCompletedActionsMenu, handleRestartThisAssessment])

  const content = consentPending ? (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0, p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Practitioner consent</Typography>
        <IconButton size="small" onClick={handleConsentCancel} aria-label="Cancel">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
          {consentPending.title}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="body2" component="div" sx={{ color: 'text.primary', '& p': { mb: 1.5 }, '& p:last-child': { mb: 0 } }}>
            <strong>Disclaimer and limitation of liability</strong>
            <p>
              This assessment form and any scores or interpretations generated by it are provided solely as an aid to clinical practice. They may contain errors or inaccuracies and are not a substitute for your professional judgment. The system provides an answer list at the end of the assessment.
            </p>
            <p>
              As a registered practitioner, you undertake to validate all results manually and to use this tool only in conjunction with your own assessment. You will not rely on the score or output of this tool to make any diagnosis or clinical decision. You agree to download the results and to calculate or verify scores yourself where appropriate.
            </p>
            <p>
              By proceeding, you confirm that you are running this assessment knowing the risks and limitations described above, and you accept full responsibility for any use of the outputs. You agree to indemnify and hold harmless the provider of this tool and its affiliates from any claim, loss, or liability arising from your use of this assessment or reliance on its outputs.
            </p>
          </Typography>
        </Paper>
        <FormControlLabel
          control={
            <Checkbox
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I have read and agree to the above. I accept the risks and will not rely on this tool for diagnosis; I will validate results and calculate scores as needed.
            </Typography>
          }
          sx={{ display: 'block', alignItems: 'flex-start', mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={handleConsentCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!consentChecked}
            onClick={() => {
              if (!consentPending || !consentChecked) return
              storeAssessmentConsent(bookedEventId, consentPending.assessmentId, consentPending.title)
              setConsentPending(null)
              setConsentChecked(false)
              if (consentPending.type === 'fromList') {
                const { payload } = consentPending
                // Assessment event page: opening the page means the assessment is already booked — never call book API; open runner directly.
                // Parent-event drawer: assessments from event list also skip book; only non-resume "available-only" items need booking.
                if (isAssessmentEventPage || payload?.isInProgress || payload?.assessment?.source === 'event') {
                  runAfterConsentResume({ clinicalOrPsych: payload.clinical, assessmentSpec: payload.assessmentSpec })
                } else {
                  doBookingAndOpenRunner(payload)
                }
              } else if (consentPending.type === 'resume') {
                runAfterConsentResume(consentPending.payload)
              } else if (consentPending.type === 'restart') {
                runAfterConsentRestart(consentPending.payload)
              }
            }}
          >
            I accept and continue
          </Button>
        </Box>
      </Box>
    </Box>
  ) : resultViewRun ? (
    <PractitionerAssessmentResultView
      triggeringBookedEventId={bookedEventId}
      assessmentId={resultViewRun.assessmentId}
      assessmentTitle={resultViewRun.assessmentTitle}
      completedRunId={resultViewRun.completedRunId ?? null}
      initialRun={resultViewRun.initialRun ?? null}
      initialAssessmentReport={resultViewRun.initialAssessmentReport ?? null}
      onBack={handleBackFromResultView}
      onClose={onClose}
      onViewAnswers={setAnswerSheetRunId}
      assessmentReport={assessmentReport}
      patientId={patientId}
      mentorId={mentorId}
      onReportSaved={handleResultViewReportSaved}
      isAssessmentEventPage={isAssessmentEventPage}
      viewMode={resultViewRun.mode ?? 'view'}
    />
  ) : answersViewRun ? (
    <PractitionerAssessmentAnswersView
      run={answersViewRun.run}
      assessmentSpec={answersViewRun.assessmentSpec}
      clinicalAssessment={answersViewRun.clinicalAssessment}
      onBack={handleBackFromAnswersView}
    />
  ) : resumedRun ? (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0, p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{drawerHeading ?? 'Assessments'}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            {resumedRun.assessmentSpec?.title ?? resumedRun.clinicalAssessment?.title ?? 'Assessment'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            You had an assessment in progress.
          </Typography>
          <Button variant="contained" onClick={() => handleStartRunner(resumedRun.clinicalAssessment ?? resumedRun.psychAssessment, resumedRun.assessmentSpec)}>
            Resume
          </Button>
          <Button sx={{ ml: 1 }} onClick={() => {
            try {
              if (resumedRun?.assessmentSpec?.assessmentId && bookedEventId) {
                sessionStorage.removeItem(getDraftKey(bookedEventId, resumedRun.assessmentSpec.assessmentId))
              }
            } catch (e) {}
            setResumedRun(null)
          }}>
            Start fresh
          </Button>
        </Box>
        <Divider sx={{ mx: 2, my: 2 }} />
        <Typography variant="body2" sx={{ px: 2, pb: 1 }} color="text.secondary">
          Other assessments — select one and click <strong>Start</strong> to book (if paid) and run.
        </Typography>
        <List disablePadding>
          {sortedDrawerList
            .filter((a) => a.id !== resumedRun?.assessmentSpec?.assessmentId)
            .map((a) => {
              const completed = completedIds.has(a.id)
              const statusItem = assessmentStatusByAssessmentId.get(a.id) ?? null
              const found = a.found
              const itemConfigured = found?.appointmentConfigured !== false
              const isRunnable = found?.runnable !== false
              const qnaLocked = found?.assessmentSpec?.qnaUnlocked === false
              const canRun = found && !completed && itemConfigured && isRunnable
              const resumable = resumableIds.has(a.id)
              const qnaLockedMessage = qnaLocked
                ? (found?.assessmentSpec?.qnaLockedReason || 'Book/complete payment for this assessment to unlock questions')
                : null
              const statusLabel = getListingStatusLabel(statusItem, completed)
              const secondaryText = statusLabel
                ? statusLabel
                : found && found.appointmentConfigured === false
                  ? (found.reason || 'Appointment not configured')
                  : found && found.runnable === false && found.runnableReason
                    ? found.runnableReason
                  : qnaLockedMessage
                    ? qnaLockedMessage
                    : a.source === 'event'
                      ? null
                      : a.source === 'available'
                        ? 'Not yet added to this session'
                        : 'Not configured for this event'
              const eventTypeLabel =
                found?.eventTypeConfigured && found?.eventType?.title ? found.eventType.title : null
              const runnableReasonText = found?.runnableReason ?? (found?.runnable === false ? 'Not runnable' : null)
              const showRunnableReason = includeNonRunnable && runnableReasonText
              const supportsAnswerSheet = !isCustomProtocolRunnerType(
                runByAssessmentId.get(a.id)?.runnerType ?? found?.assessmentSpec?.runnerType,
              )
              const secondaryNode =
                secondaryText || eventTypeLabel || showRunnableReason ? (
                  <>
                    {secondaryText}
                    {eventTypeLabel && (
                      <Typography component="span" variant="caption" display="block" color="text.secondary">
                        {eventTypeLabel}
                      </Typography>
                    )}
                    {showRunnableReason && (
                      <Typography component="span" variant="caption" display="block" color="text.secondary">
                        {runnableReasonText}
                      </Typography>
                    )}
                  </>
                ) : null
              return (
                <ListItem
                  key={a.id}
                  disablePadding
                  secondaryAction={
                    !completed && (resumable ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleSelectAssessment(a)}
                        disabled={bookingInProgress}
                        startIcon={<PlayCircleOutlineIcon />}
                      >
                        Resume
                      </Button>
                    ) : canRun ? (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSelectAssessment(a)}
                        disabled={bookingInProgress}
                        startIcon={<PlayCircleOutlineIcon />}
                      >
                        Start
                      </Button>
                      ) : null)
                  }
                  sx={(theme) => ({
                    mx: 2,
                    my: 0.75,
                    px: 0.25,
                    border: 1,
                    borderColor: completed ? alpha(theme.palette.success.main, 0.35) : 'divider',
                    borderRadius: 2,
                    bgcolor: completed ? alpha(theme.palette.success.light, 0.08) : alpha(theme.palette.background.paper, 0.9),
                    ...(completed ? { flexDirection: 'column', alignItems: 'stretch' } : {}),
                  })}
                >
                        <ListItemButton
                          onClick={() => (resumable || canRun) && ((renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, resumable ? 'resume' : 'start', undefined, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : handleSelectAssessment(a))}
                          disabled={bookingInProgress || !found || !itemConfigured || (!resumable && !isRunnable && !completed)}
                          sx={{ pr: !completed && (resumable || canRun) ? 15 : 0, borderRadius: 1.5 }}
                        >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {completed ? (
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                      ) : (
                        <PlayCircleOutlineIcon color={canRun ? 'primary' : 'disabled'} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={a.title}
                      secondary={secondaryNode}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                  {completed && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, pb: 1.5, pt: 0 }}>
                      <Button size="small" variant="contained" onClick={() => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, 'edit', undefined, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : handleEditReport(a.id, a.title)}>
                        Edit Report
                      </Button>
                      {supportsAnswerSheet && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, 'answers', undefined, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : openAnswerSheetForAssessment(a.id)}
                        >
                          View Answers
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, 'result', completedRunIdHintsByAssessmentId.get(a.id) ?? null, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : handleViewResult(a.id, a.title, null, completedRunIdHintsByAssessmentId.get(a.id) ?? null)}
                      >
                        View Result
                      </Button>
                      <IconButton
                        size="small"
                        aria-label="More actions"
                        onClick={(event) => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, null, completedRunIdHintsByAssessmentId.get(a.id) ?? null, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : openCompletedActionsMenu(event, a)}
                        sx={{ ml: 'auto' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </ListItem>
              )
            })}
        </List>
      </Box>
    </Box>
  ) : selectedRun ? (
    (() => {
      const assessmentEventId = isAssessmentEventPage
        ? bookedEventId
        : sanitizeAssessmentEventBookedEventId(selectedRun.assessmentEventBookedEventId, bookedEventId)
      if (!isAssessmentEventPage && !assessmentEventId) {
        return (
          <Box sx={{ p: 2 }}>
            <Typography color="error">Assessment booking id is missing. Please refresh or book this assessment again.</Typography>
            <Button size="small" sx={{ mt: 1 }} onClick={handleBackFromRunner}>Back to list</Button>
          </Box>
        )
      }
      return (
        isCustomProtocolAssessmentSpec(selectedRun.assessmentSpec) ? (
          <CustomProtocolAssessmentRunner
            assessmentSpec={selectedRun.assessmentSpec}
            clinicalAssessment={selectedRun.clinicalAssessment ?? selectedRun.psychAssessment}
            clinicalAssessmentId={selectedRun.clinicalAssessment?.id ?? selectedRun.psychAssessment?.id}
            psychAssessmentId={selectedRun.psychAssessment?.id ?? selectedRun.clinicalAssessment?.id}
            assessmentId={selectedRun.assessmentSpec.assessmentId}
            patientId={patientId}
            clinicId={assessmentRunnerBookedEventInfo?.clinicId ?? bookedEventInfo?.clinicId ?? null}
            bookedEventId={assessmentEventId}
            parentBookedEventId={isAssessmentEventPage ? undefined : bookedEventId}
            initialDraftRunId={selectedRun.initialDraftRunId}
            initialDraftRevision={selectedRun.initialDraftRevision}
            initialStructuredResponses={selectedRun.initialStructuredResponses}
            initialCustomRunData={selectedRun.initialCustomRunData}
            latestCompletedRunId={selectedRun.latestCompletedRunId}
            onSuccess={handleRunSuccess}
            onBack={handleBackFromRunner}
            onViewResult={handleViewResult}
            draftKey={getDraftKey(bookedEventId, selectedRun.assessmentSpec.assessmentId)}
            bookedEventInfo={assessmentRunnerBookedEventInfo}
          />
        ) : (
          <PractitionerAssessmentRunner
            assessmentSpec={selectedRun.assessmentSpec}
            clinicalAssessment={selectedRun.clinicalAssessment ?? selectedRun.psychAssessment}
            clinicalAssessmentId={selectedRun.clinicalAssessment?.id ?? selectedRun.psychAssessment?.id}
            psychAssessmentId={selectedRun.psychAssessment?.id ?? selectedRun.clinicalAssessment?.id}
            assessmentId={selectedRun.assessmentSpec.assessmentId}
            bookedEventId={assessmentEventId}
            parentBookedEventId={isAssessmentEventPage ? undefined : bookedEventId}
            isAssessmentEventPage={isAssessmentEventPage}
            initialRunAnswers={selectedRun.initialRunAnswers}
            initialDraftRunId={selectedRun.initialDraftRunId}
            initialDraftRevision={selectedRun.initialDraftRevision}
            latestCompletedRunId={selectedRun.latestCompletedRunId}
            onSuccess={handleRunSuccess}
            onBack={handleBackFromRunner}
            onViewResult={handleViewResult}
            draftKey={getDraftKey(bookedEventId, selectedRun.assessmentSpec.assessmentId)}
          />
        )
      )
    })()
  ) : (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {!renderListOnly && (
        <Box sx={{ flexShrink: 0, p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{drawerHeading ?? 'Assessments'}</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      {showList && accessAllowed && showApiDebugModeUrlUi && !renderListOnly && (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeNonRunnable}
                onChange={(e) => setIncludeNonRunnable(e.target.checked)}
              />
            }
            label="Include non-runnable"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeSelfConducted}
                onChange={(e) => setIncludeSelfConducted(e.target.checked)}
              />
            }
            label="Include self-conducted"
          />
        </Box>
      )}
      {showList && !loading && accessAllowed && drawerList.length > 0 && showApiDebugModeUrlUi && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Session assessments — select one and click <strong>Start</strong> to book (if paid) and conduct.
          </Typography>
        </Box>
      )}
      {showPaymentPending && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Waiting for payment: {paymentPending.assessmentTitle}
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={handleRefreshAfterPayment}>
            Refresh
          </Button>
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Skeleton variant="text" width="70%" height={28} sx={{ mb: 2 }} />
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={i % 2 ? '85%' : '60%'} height={24} />
                  <Skeleton variant="text" width="45%" height={18} sx={{ mt: 0.5 }} />
                </Box>
              </Box>
            ))}
            <Skeleton variant="text" width="50%" height={20} sx={{ mt: 3, mb: 1 }} />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          </Box>
        ) : !accessAllowed ? (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary">You do not have access to run assessments.</Typography>
          </Box>
        ) : allowedAssessments.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary">No assessments in your list. Check practitioner assessment settings.</Typography>
          </Box>
        ) : (
          <>
            {!bookingAvailability?.appointmentConfigured && (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor:
                    bookingAvailability?.reason === 'clinical assessment not specified'
                      ? 'action.hover'
                      : 'warning.light',
                  color:
                    bookingAvailability?.reason === 'clinical assessment not specified'
                      ? 'text.secondary'
                      : 'warning.dark',
                }}
              >
                <Typography variant="caption">
                  {bookingAvailability?.reason === 'clinical assessment not specified'
                    ? 'This session has no pre-attached clinical assessment. You can still select an assessment below and use Start to book and conduct.'
                    : bookingAvailability?.reason || 'Assessment appointment type not configured for this clinic.'}
                </Typography>
              </Box>
            )}
            {showApiDebugModeUrlUi && (
              <Typography variant="body2" sx={{ px: 2, pt: 2, pb: 1.5 }} color="text.secondary">
                Which assessment would you like to conduct? Click <strong>Start</strong> to book (if paid) and run.
              </Typography>
            )}
            {drawerListBySection.map((section, sectionIdx) => (
              <Box key={section.clinicalId}>
                {sectionIdx > 0 && (
                  <Box sx={{ py: 2 }}>
                    <Divider sx={{ borderColor: 'divider' }} />
                  </Box>
                )}
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ letterSpacing: '0.02em' }}>
                    {section.title}
                  </Typography>
                </Box>
                <List disablePadding>
                  {section.items.map((a) => {
                    const completed = completedIds.has(a.id)
                    const statusItem = assessmentStatusByAssessmentId.get(a.id) ?? null
                    const found = a.found
                    const itemConfigured = found?.appointmentConfigured !== false
                    const isRunnable = found?.runnable !== false
                    const qnaLocked = found?.assessmentSpec?.qnaUnlocked === false
                    const canRun = found && !completed && itemConfigured && isRunnable
                    const resumable = resumableIds.has(a.id)
                    const qnaLockedMessage = qnaLocked
                      ? (found?.assessmentSpec?.qnaLockedReason || 'Book/complete payment for this assessment to unlock questions')
                      : null
                    const statusLabel = getListingStatusLabel(statusItem, completed)
                    const secondaryText = statusLabel
                      ? statusLabel
                      : found && found.appointmentConfigured === false
                        ? (found.reason || 'Appointment not configured')
                        : found && found.runnable === false && found.runnableReason
                          ? found.runnableReason
                        : qnaLockedMessage
                          ? qnaLockedMessage
                          : a.source === 'event'
                            ? null
                            : a.source === 'available'
                              ? 'Not yet added to this session'
                              : 'Not configured for this event'
                    const eventTypeLabel =
                      found?.eventTypeConfigured && found?.eventType?.title ? found.eventType.title : null
                    const runnableReasonText = found?.runnableReason ?? (found?.runnable === false ? 'Not runnable' : null)
                    const showRunnableReason = includeNonRunnable && runnableReasonText
                    const supportsAnswerSheet = !isCustomProtocolRunnerType(
                      runByAssessmentId.get(a.id)?.runnerType ?? found?.assessmentSpec?.runnerType,
                    )
                    const secondaryNode =
                      secondaryText || eventTypeLabel || showRunnableReason ? (
                      <>
                        {secondaryText}
                        {eventTypeLabel && (
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            {eventTypeLabel}
                          </Typography>
                        )}
                        {showRunnableReason && (
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            {runnableReasonText}
                          </Typography>
                        )}
                      </>
                    ) : null
                    return (
                      <ListItem
                        key={a.id}
                        disablePadding
                        secondaryAction={
                          !completed && (resumable ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, 'resume', undefined, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : handleSelectAssessment(a)}
                              disabled={bookingInProgress}
                              startIcon={<PlayCircleOutlineIcon />}
                            >
                              Resume
                            </Button>
                          ) : canRun ? (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, 'start', undefined, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : handleSelectAssessment(a)}
                              disabled={bookingInProgress}
                              startIcon={<PlayCircleOutlineIcon />}
                            >
                              Start
                            </Button>
                          ) : null)
                        }
                        sx={(theme) => ({
                          mx: 2,
                          my: 0.75,
                          px: 0.25,
                          border: 1,
                          borderColor: completed ? alpha(theme.palette.success.main, 0.35) : 'divider',
                          borderRadius: 2,
                          bgcolor: completed ? alpha(theme.palette.success.light, 0.08) : alpha(theme.palette.background.paper, 0.9),
                          ...(completed ? { flexDirection: 'column', alignItems: 'stretch' } : {}),
                        })}
                      >
                        <ListItemButton
                          onClick={() => (resumable || canRun) && ((renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, resumable ? 'resume' : 'start', undefined, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : handleSelectAssessment(a))}
                          disabled={bookingInProgress || !found || !itemConfigured || (!resumable && !isRunnable && !completed)}
                          sx={{ pr: !completed && (resumable || canRun) ? 15 : 0, py: 1.5, borderRadius: 1.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 44 }}>
                            {completed ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                            ) : (
                              <PlayCircleOutlineIcon color={canRun ? 'primary' : 'disabled'} sx={{ fontSize: 28 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={a.title}
                            secondary={secondaryNode}
                            primaryTypographyProps={{ fontWeight: 600, variant: 'body1' }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItemButton>
                        {completed && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, pb: 1.5, pt: 0, pl: 6 }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, 'edit', undefined, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : handleEditReport(a.id, a.title)}
                            >
                              Edit Report
                            </Button>
                            {supportsAnswerSheet && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, 'answers', undefined, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : openAnswerSheetForAssessment(a.id)}
                              >
                                View Answers
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, 'result', completedRunIdHintsByAssessmentId.get(a.id) ?? null, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : handleViewResult(a.id, a.title, null, completedRunIdHintsByAssessmentId.get(a.id) ?? null)}
                            >
                              View Result
                            </Button>
                            <IconButton
                              size="small"
                              aria-label="More actions"
                              onClick={(event) => (renderListOnly && onListActionClick) ? onListActionClick(a.id, a.title, null, completedRunIdHintsByAssessmentId.get(a.id) ?? null, section?.title ?? a.found?.clinicalAssessment?.title ?? a.found?.psychAssessment?.title) : openCompletedActionsMenu(event, a)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </ListItem>
                    )
                  })}
                </List>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  )

  const innerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 0.75,
          backgroundColor: (t) => alpha(t.palette.info.main, 0.15),
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" fontWeight={700} color="info.dark">
          Beta — Experimental
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {content}
        {(bookingInProgress || startingRunner) && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.97),
                zIndex: 10,
              }}
            >
              {loadingAssessmentDisplay?.coverIcon || loadingAssessmentDisplay?.icon ? (
                <Box
                  component="img"
                  src={loadingAssessmentDisplay.coverIcon || loadingAssessmentDisplay.icon}
                  alt=""
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: 'contain',
                    borderRadius: 2,
                  }}
                />
              ) : (
                <AssignmentIcon sx={{ fontSize: 64, color: 'primary.main', opacity: 0.8 }} />
              )}
              <Box sx={{ textAlign: 'center', px: 3 }}>
                {loadingAssessmentDisplay?.title && (
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    {loadingAssessmentDisplay.title}
                  </Typography>
                )}
                <Typography variant="body1" fontWeight={600} color="text.primary">
                  Preparing the assessment…
                </Typography>
                {loadingAssessmentDisplay?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {loadingAssessmentDisplay.description}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Loading your questions. This may take a moment.
                </Typography>
              </Box>
              <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main' }} />
            </Box>
          )}
        </Box>
        <PractitionerAssessmentAnswerSheetModal
          open={Boolean(answerSheetRunId)}
          runId={answerSheetRunId}
          onClose={() => setAnswerSheetRunId(null)}
        />
        <Menu
          anchorEl={completedActionsAnchorEl}
          open={Boolean(completedActionsAnchorEl)}
          onClose={closeCompletedActionsMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={handleRedoFromCompletedMenu}
          >
            Redo Assessment (Update Answers)
          </MenuItem>
        </Menu>
    </Box>
  )

  if (renderListOnly) {
    if (loading) {
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" width="70%" height={28} sx={{ mb: 2 }} />
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="85%" height={24} />
                <Skeleton variant="text" width="45%" height={18} sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )
    }
    if (!accessAllowed) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="text.secondary">You do not have access to run assessments.</Typography>
        </Box>
      )
    }
    if (!apiList?.length) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="text.secondary">No assessments in your list.</Typography>
        </Box>
      )
    }
    return <Box sx={{ flex: 1, overflow: 'auto', minHeight: 200 }}>{content}</Box>
  }

  if (contentOnly) {
    return innerContent
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: effectiveDrawerWidth }, maxWidth: '100%' },
      }}
    >
      {innerContent}
    </Drawer>
  )
}
